import { useLiveReviewDraft } from "../lib/useLiveReviewDraft";
import { readRecordPhoto } from "../lib/recordPhoto";
import { AutodeskAnswerPreview, AutodeskKindChip, autodeskKindOf, dailyReportHref, isAutodeskIssue } from "./AutodeskReviewParts";
import {
  DecidedRow,
  SavedForLaterHeader,
  ProcoreKindChip,
  ReviewDoneCard,
  countPhrase,
  decidedKind,
  PHONE_MARK_TOKENS,
  phoneMarkInputs,
  phoneMarkValue,
  procoreKindOf,
  type DecidedState,
} from "./ReviewCardParts";
import type { AutodeskReviewDestinations } from "../../server/queries/getReviewQueue";
import { PROCORE_TASK_STATUSES, normalizeProcoreTaskStatus } from "../../../../backend/src/shared/procoreTaskStatus";
import { updateDirectoryFields } from "../lib/assigneeCompanyTrade";
import { RecordPhotoPicker } from "../components/RecordPhotoPicker";
import { ReviewCompletion, syncOutcome, useReviewSyncCounts, type ReviewCompletionData } from "../components/ReviewCompletion";
/**
 * The Review queue — a walk list beside one walk's records, per the canvas
 * design "Led AI · Review Queue — desktop".
 *
 * ## What changed, and why the old shape had to go
 *
 * This page used to be one long list grouped by review STATUS: a single
 * heading, "Not reviewed", over every pending record in every walk on every
 * project. That heading could never say anything else, the rows had nothing to
 * belong to, and the one question a reviewer actually asks — "am I done with
 * this walk?" — had no answer anywhere on the screen.
 *
 * The design's answer is the unit a person reviews IN: a walk. The left column
 * lists walks with records waiting; the right pane holds one walk's records,
 * one card each, with a progress bar that says how much of that walk is done.
 * `getReviewQueue` groups the same rows by walk (`walks`) so the grouping is
 * the server's, not a re-derivation here.
 *
 * ## Every card shows what the record was BUILT FROM
 *
 * A reviewer is being asked whether a generated record is right, so the card
 * carries the evidence beside the claim: the record's fields (through the
 * MOBILE schema — `mobileRecordPresentation.ts` — so the console never
 * maintains a second field vocabulary), its photos, and what was said on the
 * walk that produced it. Reviewing a paraphrase against nothing is what the
 * old rows asked for.
 *
 * Review cards intentionally omit resolver warnings. An uncertain assignee
 * belongs in the assignee dropdown where it can be corrected, not in a red
 * message that adds no action.
 *
 * ## What the design draws that this deliberately does NOT
 *
 *  - **The field pickers.** Free-form fields edit in place and constrained
 *    values use dropdowns. Punch changes autosave and sync across devices
 *    before Accept; other record kinds submit their draft with the decision.
 *  - **"Wrong type? Change from Punch item".** Same reason: there is no route
 *    that re-kinds a record.
 *
 * Everything the console CAN do keeps the design's own controls: Reject,
 * Later (a real `deferred` decision the whole chain accepts) and Accept, as
 * the design's pills.
 *
 * ## The queue is still YOUR UNREVIEWED DAILY LOGS, and the page still says so
 *
 * Spec §5 fixed three filters on the server: not-yet-reviewed, the caller's
 * own, and `log` kind (`getReviewQueue.ts` carries the reasoning). So the
 * empty state names whose queue this is — "Nothing to review" over a
 * workspace full of a teammate's unreviewed records would read as a bug — and
 * there is no kind strip, because its only control could not change the list.
 *
 * ## The controls are LIVE, and still disable when they cannot be
 *
 * `writesConfigured` false means this deployment has nowhere to send a
 * decision. Every control is then genuinely `disabled` and the reason is
 * printed once at the top rather than only in a tooltip:
 * `backendGateway.ts`'s rule — "a mutation control that appears live and
 * quietly does nothing is the single worst outcome available here" — is the
 * whole reason this page states its own limits.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  fetchReviewQueue,
  fetchItemDetail,
  fetchItemAssignees,
  postMutation,
  postItemEdit,
  deleteMutation,
  fetchItemEditOptions,
  itemEditCategory,
  postReviewRestore,
  type ApiError,
  type ConsoleRow,
  type ItemEditDirectoryOption,
  type ItemEditOptions,
  type ReviewQueueResponse,
  type ReviewRowResponse,
  type ReviewWalkResponse,
  type ReviewDecidedRowResponse,
} from "../api";
import { UNDO_WINDOW_MS } from "../../../../backend/src/shared/undoWindow";
import { readManpowerCount } from "../../../../backend/src/shared/manpowerMerge";
import { DATA_CHANGED_EVENT } from "../resourceCache";
import { Property, PropertyEditorStyles, specFor } from "./RecordDetail";
import { extendRecordFields, isMobileObservation } from "./recordDetailFields";
import type { PropertyState } from "../components/PropertyRows";
import {
  CHIP_FONT,
  FIELD_NAME_STYLE,
  Mark,
  fieldsNeedingTheirName,
  isAlarmingLevel,
  markFor,
  valueMarksFor,
  type RecordChipFieldType,
} from "../../../../backend/src/frontend/components/record/propertyMarks";
import { AssigneeAvatar } from "../../../../backend/src/frontend/components/shared/assignee/AssigneeAvatar";
import { useCoalescedResource } from "../useResource";
import { ADR, C, FONT_MONO, FONT_TEXT, RD, RV } from "../tokens";
import { Building2, Camera, Plus, MinusCircle, Undo2, UserRound } from "lucide-react";
import { AssigneePicker } from "../components/AssigneePicker";
import { NameMatchPill, nameMatchOf, nameMatchQuery, type NameMatchCandidate } from "../components/NameMatchPill";
import { ManpowerConflictPanel, manpowerConflictActionLabel, manpowerConflictChoice, manpowerConflictSubmission, type ManpowerMergeResolution } from "../components/ManpowerConflictPanel";
import {
  CopyValueButton,
  ASK_COMPOSER_CLEARANCE,
  MONTHS,
  ConsoleViewProps,
  EmptyPanel,
  EvidenceTile,
  FailurePanel,
  KIND_LABEL,
  META,
  Pill,
  StaleBanner,
  READING_COLUMN_WIDTH,
  clockOf,
  dayLabel,
  plural,
  whenStamp,
} from "../components/chrome";
import { SkeletonBlock } from "../components/Skeleton";
import {
  mobileRecordDefinition,
  mobileRecordFields,
  mobileRecordKindLabel,
} from "../mobileRecordPresentation";
import { AnchoredPanel } from "../components/AnchoredPanel";
import { FieldCombobox } from "../components/FieldCombobox";
import { Calendar } from "../components/Calendar";
import { PhotoFullscreenViewer, type FullscreenPhoto } from "../components/PhotoFullscreenViewer";
import { ReviewDecisions } from "./reviewDecisions";
import type { ReviewDecision } from "../api";
import { reviewCountNote } from "./reviewCountNote";
import {
  DAILY_LOG_STATUS_OPTIONS,
  DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS,
  OBSERVATION_PRIORITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
  PROCORE_DELAY_TYPE_OPTIONS,
  isRequiredProcoreDailyLogFieldValuePresent,
  type ProcoreDailyLogFieldDefinition,
  type ProcoreDailyLogFieldValue,
} from "../../../../backend/src/shared/procoreDailyLog";
import { TimeField } from "../components/TimeField";
import { EMPTY_WALK_PHOTOS_DRAFT, WalkPhotosPane, type WalkPhotosDraft } from "./WalkPhotosPane";
import { WALK_PHOTO_FRESH_CLASS, WalkPhotoOriginStrip, WalkPhotoOriginTag, useWalkPhotoOrigin, walkPhotosOutcome, withoutOriginPhoto } from "./WalkPhotoOrigin";
import { isWalkPhotosStepCard, walkPhotosIntegrationOf } from "../../../../backend/src/frontend/pages/visit/walkPhotosModel";
import { integrationName } from "../lib/integrationName";

/** The design's queue column ("Review", 264px), to the pixel. */
const WALK_COLUMN = 264;
/**
 * Review's record column — the READING measure, not the table one.
 *
 * ## Why it is not 1180
 *
 * It was, to match Observations and Punch. The trouble is that the match was
 * arithmetic rather than visual, and it never actually bound: Review is a
 * split pane, so the record column gets the pane MINUS the 352px picker minus
 * its gutters, and at every ordinary window size that is already less than
 * 1180. At 1800px the card ran to 24px from the right edge of the screen
 * while Walks — the page it was supposedly aligned with — stopped 254px
 * short. The cap only started doing anything past about 2200px wide.
 *
 * So the number was doing no work at the widths anyone uses, and the card
 * was simply "whatever is left", which is how a form ends up 1144px wide.
 *
 * 1180 is a measure for a dense TABLE, where ten columns need the room and
 * the eye travels down one of them. A review card is not that: it is a title,
 * a field table read left-to-right, and a 250px media column. At 1144 a
 * field's label sat at one edge and its value at the other with 700px of
 * nothing between them.
 *
 * The card is a run of pills and a paragraph now ("Daily Report Editor",
 * review screen), and the design sets its column at 800. Wider, the pills
 * spread into one long thin row, the paragraph runs past a comfortable line,
 * and the tray is half empty. Narrower than the console's 980 reading column,
 * so it is the design's number that holds.
 *
 * The 2026-10 redesign ("Review", LED-1153) narrows the walk column to 600,
 * centred, with every record its own stacked card.
 */
const REVIEW_CONTENT_WIDTH = Math.min(READING_COLUMN_WIDTH, 600);

function ReviewRecordSkeleton() {
  // The loaded card's geometry: chip and stamp, title, the field tray, the
  // media strip, the decision footer.
  return (
    <div
      style={{
        background: ADR.paper,
        border: "1px solid rgba(20,17,12,.08)",
        borderRadius: 22,
        boxShadow: "0 10px 30px rgba(20,17,12,.06)",
        padding: "22px 24px 0",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SkeletonBlock height={34} width={132} radius={17} />
        <SkeletonBlock height={10} width={110} radius={4} />
      </div>
      <SkeletonBlock height={30} width="62%" radius={6} style={{ marginTop: 18 }} />
      <SkeletonBlock height={100} radius={14} style={{ marginTop: 16 }} />
      <SkeletonBlock height={16} width="84%" radius={4} style={{ marginTop: 16 }} />
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <SkeletonBlock height={150} width={150} radius={14} />
        <SkeletonBlock height={150} width={150} radius={14} />
      </div>
      <div style={{ height: 82, margin: "20px -24px 0", borderTop: "1px solid rgba(20,17,12,.06)" }} />
    </div>
  );
}

/** Review is a split pane, so its loader reserves the same picker, header,
 * and record-card columns the loaded queue uses. */
function ReviewQueueSkeleton() {
  return (
    <div data-testid="review-workspace" data-skeleton="review" role="status" aria-label="Loading review queue" aria-busy="true" style={{ flex: 1, minHeight: 0, display: "flex", background: C.pageGround }}>
      <div style={{ width: WALK_COLUMN, flexShrink: 0, background: C.surface, borderRight: `1px solid ${C.line}`, padding: "26px 22px 16px" }}>
        <SkeletonBlock height={24} width={164} radius={6} />
        <SkeletonBlock height={10} width={194} radius={4} style={{ marginTop: 10 }} />
        <SkeletonBlock height={10} width={242} radius={4} style={{ marginTop: 12 }} />
        <SkeletonBlock height={42} radius={11} style={{ marginTop: 18 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 28 }}>
          {[0, 1, 2, 3, 4, 5].map((index) => <SkeletonBlock key={index} height={74} radius={12} />)}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${C.line}` }}>
          <div style={{ width: "100%", maxWidth: REVIEW_CONTENT_WIDTH, margin: "0 auto" }}>
            <SkeletonBlock height={10} width={182} radius={4} />
            <SkeletonBlock height={28} width={176} radius={6} style={{ marginTop: 9 }} />
            <SkeletonBlock height={5} radius={3} style={{ marginTop: 17 }} />
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: "16px 24px" }}>
          <div style={{ width: "100%", maxWidth: REVIEW_CONTENT_WIDTH, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
            <ReviewRecordSkeleton />
            <ReviewRecordSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The redesign's pointer and press states (LED-1153/1155/1156). States the
 *  stylesheet owns are never also set inline — an inline background would
 *  outrank them. */
const REVIEW_CSS = `
  .rv-walk { transition: background .14s ease, border-color .14s ease; }
  .rv-walk[data-active="false"]:hover { background: ${RV.rowHover}; }
  .rv-walk:active, .mp-choice:active, .name-match-face:active, [data-testid="review-decision"]:enabled:active { transform: scale(.97); }
  .mp-choice { transition: background .14s ease, box-shadow .14s ease, color .14s ease; }
  .mp-choice:enabled:hover[aria-checked="false"] { color: ${C.ink}; }
  .name-match-face { transition: transform .14s ease, box-shadow .14s ease; }
  .name-match-face:enabled:hover { box-shadow: 0 0 0 1.5px ${C.ink}; }
  .name-match-open:enabled:hover { text-decoration: underline; text-decoration-color: rgba(20,17,12,.25); text-underline-offset: 3px; }
`;

/** Which walk is on screen. In the URL, like every other console state, so a
 *  reviewer can send "this walk" to somebody. */
const REVIEWING_PARAM = "reviewing";

/** Which way the queue is travelling when a different walk is picked. */
type SlideDir = "down" | "up";

/** A decided record as the pane folds it. `at` is when this page made the
 *  decision; `local` marks one the server has not listed yet. */
type DecidedEntry = ReviewDecidedRowResponse & { at?: number; local?: boolean };

/** A decision made on this page, held until the queue re-reads it. */
interface LocalDecision {
  walkKey: string;
  /** Client clock; compared with the read's `generatedAt`. */
  at: number;
  row: ReviewDecidedRowResponse;
}

/** How long the outgoing cards take to leave before the next walk is shown.
 *  Must match `.revout-*` in `main.tsx`. */
const SLIDE_OUT_MS = 140;

/** How long a decided card takes to swipe off. Must match `.revswipe-*`. */
const SWIPE_MS = 280;

/**
 * Whether this reader has asked for less motion, at the moment of asking.
 *
 * Every animation in the console is declared inside
 * `@media (prefers-reduced-motion: no-preference)`, so CSS already withholds
 * itself. This is for the JS half — the two places that WAIT for an animation
 * before doing something (the walk switch, and a decision leaving the queue).
 * Those waits have to disappear along with the animation, or the console would
 * feel slower to exactly the people who asked for less of it and would be
 * pausing for something they never see.
 */
function reduced(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// ---------------------------------------------------------------------------
// Time, the way the design says it
// ---------------------------------------------------------------------------

/** `8:02 AM` — the stamp the design prints on a photo tile. Written out
 *  rather than reusing `clockOf` (`8:02a`), which is the console's list
 *  voice: on a tile the meridiem is the only thing separating two photos
 *  taken twelve hours apart. */
function photoStamp(capturedAt: string | Date): string {
  const at = typeof capturedAt === "string" ? new Date(capturedAt) : capturedAt;
  if (Number.isNaN(at.getTime())) return "";
  const hours = at.getHours();
  return `${hours % 12 || 12}:${String(at.getMinutes()).padStart(2, "0")} ${
    hours < 12 ? "AM" : "PM"
  }`;
}

/** The design's camera mark, over the media column. */
function CameraGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path
        d="M3 8.5h3.2l1.4-2.2h8.8l1.4 2.2H21v10H3v-10Z"
        stroke={C.sub2}
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.4" stroke={C.sub2} strokeWidth="1.7" />
    </svg>
  );
}

/** `Today · 7:15a`, `Yesterday · 3:08p`, or `Mon, Aug 25 · 2:12p`. */
function walkWhen(startedAt: string | undefined, now: Date): string {
  if (!startedAt) return "No walk";
  const at = new Date(startedAt);
  const days = wholeDaysBetween(at, now);
  const clock = clockOf(at);
  if (days === 0) return `Today · ${clock}`;
  if (days === 1) return `Yesterday · ${clock}`;
  return `${dayLabel(startedAt)} · ${clock}`;
}

function wholeDaysBetween(then: Date, now: Date): number {
  const startOf = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.max(0, Math.round((startOf(now) - startOf(then)) / 86_400_000));
}

// ---------------------------------------------------------------------------
// Left column — the walks with records waiting
// ---------------------------------------------------------------------------

/**
 * One walk in the queue (design "Review", queue column): a count tile, the
 * walk's title, and `DATE · PROJECT` under it.
 *
 * The OPEN walk wears a gold tile and a gold outline — the queue's one
 * primary mark. A walk with nothing left turns its tile green (it stays
 * listed while it is on screen, see `retainedRef`). A walk with only records
 * saved for later counts those, under LATER.
 */
function WalkCard({
  walk,
  active,
  onSelect,
}: {
  walk: ReviewWalkResponse;
  active: boolean;
  onSelect: () => void;
}) {
  const laterOnly = walk.total === 0 && !!walk.deferred;
  const done = walk.total === 0 && !walk.deferred;
  const day = walk.startedAt ? dayWithYear(walk.startedAt) : null;
  return (
    <button
      type="button"
      className="kb rv-walk"
      data-testid="review-walk-card"
      data-walk-key={walk.key}
      data-active={active ? "true" : "false"}
      data-done={done ? "true" : "false"}
      aria-current={active ? "true" : undefined}
      onClick={onSelect}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        textAlign: "left",
        padding: "9px 8px",
        borderRadius: 12,
        boxSizing: "border-box",
        // The outline rides inside the row's own box (transparent at rest),
        // so selecting a walk never shifts the list.
        border: `1.5px solid ${active ? RV.rowGold : "transparent"}`,
        // Unset at rest: an inline background would outrank `.rv-walk:hover`.
        ...(active ? { background: ADR.card } : {}),
        fontFamily: "inherit",
        color: C.ink,
        cursor: "pointer",
      }}
    >
      <span
        data-testid="review-walk-count"
        data-tone={active ? "open" : done ? "done" : "rest"}
        style={{
          flex: "none",
          width: 32,
          height: 38,
          borderRadius: 8,
          background: active ? RV.tileGold : done ? RV.tileDone : RV.tileRest,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 800, lineHeight: 1, color: done ? RV.tileDoneInk : C.ink }}>
          {laterOnly ? walk.deferred : walk.total}
        </span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 6.5, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: active ? RV.tileGoldSub : done ? RV.tileDoneInk : ADR.label, marginTop: 2 }}>
          {laterOnly ? "later" : "left"}
        </span>
      </span>

      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          data-testid="review-walk-title"
          style={{
            display: "block",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "-0.2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {walk.walkName || "Untitled walk"}
        </span>
        {/* `DATE · PROJECT` — walks on one project are often named alike;
            the day is what tells them apart. */}
        <span
          style={{
            display: "block",
            fontFamily: FONT_MONO,
            fontSize: 8.5,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color: RV.meta,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {day && <span data-testid="review-walk-day">{`${day} · `}</span>}
          <span data-testid="review-walk-project">{walk.siteName || walk.jobSiteId}</span>
        </span>
      </span>

      {/* The chevron marks the open walk, and only the open walk. */}
      {active && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
          <path d="M9.5 6l6 6-6 6" />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// The record card — the claim, and what it was built from
// ---------------------------------------------------------------------------

/**
 * The design's field table: a label column and an inline editor.
 *
 * ## Shared with the post-review page, which is why it takes an ITEM
 *
 * It used to take the whole `ReviewRowResponse` and read three fields off
 * it. Item detail needs the same table — the user's words: *every item should
 * just be an editable state, mirror the review stage UI for post-review
 * items* — and it has no review row to hand over, only the record. Narrowing
 * the prop is what made one table serve both, instead of the second copy that
 * had already drifted into stacked labels and full-width selects.
 *
 * ## `copyable` is for the page that had copy icons before this one arrived
 *
 * Item detail's rows copy their value; the review queue's do not. Rather than
 * two components, the affordance is a flag — the review card is a decision
 * surface and a copy button on every row of it is one more thing to miss the
 * Accept button with.
 */
export function FieldRows({
  item,
  search,
  onPatchChange,
  copyable = false,
  flush = false,
  compact = false,
  onCommit,
  validationFieldKeys = [],
  layout = "table",
  reviewSet = false,
  proseOnly = false,
  liveDraft,
}: {
  /** Review autosave owns these values, including incoming device edits. */
  liveDraft?: Record<string, unknown>;
  /**
   * The review card's own field set (LED-1153): a task shows Assignee, Due
   * date and Priority; a punch item Priority, Due date, Location and
   * Assignee — in that order. Other kinds keep their schema's fields. The
   * record pages keep the full set; only the card narrows it.
   */
  reviewSet?: boolean;
  /** Only the record's own words (`pills` layout): a manpower conflict
   *  card draws its fields in the conflict panel instead (LED-1156). */
  proseOnly?: boolean;
  item: ConsoleRow;
  search: string;
  onPatchChange: (patch: Record<string, unknown>) => void;
  /**
   * `table` is the label/value list item detail and the daily-log editor
   * show. `pills` is the review card's ("Daily Report Editor", review
   * screen): every short field a pill in one tray, its glyph and a mono label
   * leading the value, and the record's own words below as plain prose.
   */
  layout?: "table" | "pills";
  /** Render a copy button at the right of every row. */
  copyable?: boolean;
  /**
   * Drop the recess and sit directly on the host surface.
   *
   * The border-and-inset is right on the REVIEW card, where the table is one
   * of several things inside a card and the recess is what separates them.
   * It is wrong on item detail, where the table IS the card's content: there
   * it drew a second rounded box a few pixels inside the first, and the read-
   * only facts underneath — which are the same table continued — sat outside
   * it, so one list of properties was split across two containers with
   * different edges.
   */
  flush?: boolean;
  /** Narrow daily-log side panel, with stacked prose and smaller gutters. */
  compact?: boolean;
  /** A directory selection is a complete decision. Item detail uses this to
   * flush its debounced write because portalled menus do not blur its form. */
  onCommit?: () => void;
  /** Fields refused by the server on the last review attempt. */
  validationFieldKeys?: string[];
}) {
  const row = { item };
  const all = fieldsFor(row.item);
  const fields = (reviewSet ? reviewCardFields(row.item, all) : all).sort((a, b) => compact ? Number(a.multiline) - Number(b.multiline) : 0);
  // The design hides some pills, but assignee selection must still update
  // company/trade and preserve those fields in the submitted patch.
  const editableFields = [...all, ...fields.filter(field => !all.some(entry => entry.key === field.key))];
  const mobileDefinition = mobileRecordDefinition(row.item);
  const [localValues, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(editableFields.map((field) => [field.key, field.rawValue])),
  );
  const values = liveDraft
    ? Object.fromEntries(editableFields.map(field => [field.key,
        field.patchKey ? String(liveDraft[field.patchKey] ?? "") : field.rawValue]))
    : localValues;
  const remoteValues = Object.fromEntries(editableFields.map(field => [field.key, field.rawValue]));
  const previousRemoteValues = useRef(remoteValues);
  useEffect(() => {
    const previous = previousRemoteValues.current;
    previousRemoteValues.current = remoteValues;
    if (item.kind !== "punch") return;
    setValues(current => Object.fromEntries(Object.entries(remoteValues).map(([key, value]) =>
      [key, current[key] === previous[key] ? value : current[key] ?? value])));
  }, [JSON.stringify(remoteValues)]);
  const displayValues = { ...values };
  for (const field of mobileRecordFields({ ...item, fieldValues: values }) ?? []) {
    if (field.calculated) displayValues[field.key] = field.value === "Not set" ? "" : field.value;
  }
  const [localReferences, setReferences] = useState<
    Partial<Record<"location" | "assignee" | "trade" | "company", ItemEditDirectoryOption | null>>
  >({});
  const references = liveDraft && "selectedAssigneeRemoteId" in liveDraft
    ? { ...localReferences, assignee: {
        remoteId: String(liveDraft.selectedAssigneeRemoteId ?? ""),
        name: String(liveDraft.selectedAssigneeName ?? liveDraft.assignee ?? ""),
        additionalAssignees: liveDraft.additionalAssignees as ItemEditDirectoryOption["additionalAssignees"],
      } }
    : localReferences;
  const [editOptions, setEditOptions] = useState<ItemEditOptions | null>(null);
  const [isLoadingEditOptions, setIsLoadingEditOptions] = useState(false);
  const [editOptionsFailed, setEditOptionsFailed] = useState(false);
  const [editOptionsError, setEditOptionsError] = useState<string | undefined>(undefined);
  const editOptionsRequested = useRef(false);
  /** The pill whose editor is open (`layout="pills"`); one at a time. */
  const [openKey, setOpenKey] = useState<string | null>(null);

  /**
   * The location's ancestry — out of the stored value, not out of a lookup.
   *
   * I had this reading the picked option's `path`, then the directory's,
   * and said an observation at rest simply could not show it. Both were
   * wrong. Procore names a location by its whole path,
   * `Tower A>Level 02>East Wing`, and THAT is what the record stores — so
   * the ancestry was in the value on the page the whole time, rendered as a
   * truncated string where the room should be. `ItemEditDirectoryOption.path`
   * is the backend's `nodeName`, which is the leaf, so reading it as the
   * second line printed the room twice.
   *
   * It needs no directory, no pick and no network, and it is right at rest.
   */
  const locationPath = splitLocation(values.location ?? item.location?.name).ancestry;

  useEffect(() => {
    setValues(
      Object.fromEntries(editableFields.map((field) => [field.key, field.rawValue])),
    );
    setReferences({});
    setEditOptions(null);
    setEditOptionsFailed(false);
    setEditOptionsError(undefined);
    setIsLoadingEditOptions(false);
    editOptionsRequested.current = false;
  }, [row.item.id, row.item.jobSiteId, row.item.category, search]);

  /**
   * The project directory, fetched once per card on first open.
   *
   * The failure is REMEMBERED rather than swallowed. It used to be
   * `.catch(() => setEditOptions(null))`, which left the panel saying "No
   * choices available" — the same words an empty directory produces — so a
   * reader whose fetch fell over retried a dropdown that was never going to
   * fill, and nothing on screen said why.
   */
  const loadEditOptions = () => {
    if (editOptionsRequested.current) return;
    editOptionsRequested.current = true;
    setIsLoadingEditOptions(true);
    // A task needs task directories even when it retains its source note's
    // category; daily logs still need their category-specific directories.
    void fetchItemEditOptions(
      row.item.jobSiteId,
      itemEditCategory(row.item),
      search,
      row.item.visitId,
    )
      .then((options) => {
        setEditOptions(options);
        setEditOptionsFailed(false);
      })
      .catch((error: unknown) => {
        setEditOptions(null);
        setEditOptionsFailed(true);
        // The backend's own sentence — "Procore is not connected for this
        // user", an expired connection, whichever it was. It is the only
        // thing on screen that can tell the reader whether retrying is
        // worth anything.
        setEditOptionsError(
          error instanceof Error && error.message ? error.message : undefined,
        );
      })
      .finally(() => setIsLoadingEditOptions(false));
  };

  const update = (
    field: ReviewField,
    value: string,
    reference?: ItemEditDirectoryOption,
  ) => {
    const { values: nextValues, references: nextReferences } = updateDirectoryFields(
      editableFields, field, value, reference, values, liveDraft ? {} : references, editOptions,
    );
    setValues(nextValues);
    setReferences(nextReferences);
    onPatchChange(
      buildReviewPatch(
        row.item,
        editableFields,
        nextValues,
        nextReferences,
        mobileDefinition?.fields,
      ),
    );
  };
  if (fields.length === 0) return null;

  const isRequired = (field: ReviewField) =>
    (item.syncSource === "procore" && field.definition?.requiredInProcore === true) ||
    validationFieldKeys.includes(field.key);
  const isInvalid = (field: ReviewField) =>
    validationFieldKeys.includes(field.key) && !!field.definition &&
    !isRequiredProcoreDailyLogFieldValuePresent(
      field.definition,
      valueForPatch(field.definition, values[field.key] ?? ""),
    );
  const requiredIn = integrationName(item.syncSource);
  const requiredLabel = requiredIn ? `Required in ${requiredIn}` : "Required";
  const fieldLabel = (field: ReviewField) => <>
    {field.label}
    {isRequired(field) && <span
      data-testid="review-field-required"
      role="img"
      aria-label={requiredLabel}
      title={requiredLabel}
      style={{ color: C.red, marginLeft: 4, fontWeight: 700 }}
    >*</span>}
  </>;

  const directoryOptionsFor = (field: ReviewField) =>
    field.reference === "location"
      ? editOptions?.locations
      : field.reference === "assignee"
        ? editOptions?.assignees
        : field.reference === "trade"
          ? editOptions?.trades
          : field.reference === "company"
            ? editOptions?.companies
            : undefined;

  if (layout === "pills") {
    const short = proseOnly ? [] : fields.filter((field) => !field.multiline);
    const prose = fields.filter((field) => field.multiline);
    // The record page's editors, over this card's own state: one value map,
    // so every edit still lands in the whole-record review patch.
    const state: PropertyState = {
      item: row.item,
      search,
      fields,
      values,
      references,
      // A name typed on a project with no directory is still the assignee
      // the review sends — the old picker's contract (`selectedAssigneeName`).
      update: (field, next, reference) =>
        update(field, next, reference ?? (field.reference === "assignee" && next && item.assigneeDirectory === false ? { remoteId: "", name: next } : undefined)),
      loadOptions: loadEditOptions,
      directoryFor: (reference) => directoryOptionsFor({ reference } as ReviewField) ?? [],
      optionsLoading: isLoadingEditOptions,
      optionsFailed: editOptionsFailed,
      optionsError: editOptionsError,
      observationTypes: editOptions?.observationTypes ?? [],
    };
    // The phone's rule for when a chip must also print its field's name: its
    // mark cannot tell it apart from the others ON THIS CARD (a number, a
    // date, a switch, a classification, a mark used twice).
    const named = fieldsNeedingTheirName(
      short.map((field) => ({ key: field.key, label: field.label, value: displayValues[field.key] ?? "", glyph: phoneMarkInputs(field).glyph, editable: !field.calculated })),
      Object.fromEntries(short.map((field) => [field.key, phoneMarkInputs(field).type ?? "text"])) as Record<string, RecordChipFieldType>,
    );
    return (
      <div data-testid="review-fields" data-layout="pills" style={{ minWidth: 0 }}>
        {short.length > 0 && (
          <div data-testid="review-field-tray" style={{ ...PHONE_MARK_TOKENS, display: "flex", flexWrap: "wrap", gap: 8, padding: 10, borderRadius: 14, background: ADR.tray }}>
            <PropertyEditorStyles />
            {short.map((field) => (
              <ReviewPill
                key={field.key}
                field={field}
                raw={displayValues[field.key] ?? ""}
                state={state}
                item={row.item}
                nameIt={named.has(field.key)}
                required={isRequired(field)}
                requiredLabel={requiredLabel}
                invalid={isInvalid(field)}
                open={openKey === field.key}
                onOpenChange={(next) => setOpenKey(next ? field.key : null)}
                onCommit={() => onCommit?.()}
                onType={(next) => update(field, next)}
              />
            ))}
          </div>
        )}
        {prose.map((field, index) => {
          // One paragraph is the record's own words and reads unlabeled, as
          // in the design; beside a second one, or when Procore requires it,
          // each says which it is.
          const labeled = prose.length > 1 || isRequired(field);
          return (
            <div
              key={field.key}
              data-testid="review-field"
              data-field={field.label.toLowerCase()}
              role="group"
              aria-label={field.label}
              aria-invalid={isInvalid(field) || undefined}
              style={{ marginTop: short.length > 0 || index > 0 ? 12 : 0 }}
            >
              {labeled && (
                <span style={{ display: "block", marginBottom: 4, fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, letterSpacing: "1.1px", color: C.sub, textTransform: "uppercase" }}>
                  {fieldLabel(field)}
                </span>
              )}
              <AutoTextarea
                label={field.label}
                testId={`review-field-editor-${field.key}`}
                value={displayValues[field.key] ?? ""}
                onChange={(next) => update(field, next)}
                className="review-editable"
                placeholder={labeled ? undefined : "Add description…"}
                style={{
                  width: "calc(100% + 12px)",
                  boxSizing: "border-box",
                  margin: "0 -6px",
                  border: "none",
                  outline: "none",
                  borderRadius: 6,
                  fontFamily: FONT_TEXT,
                  fontSize: 14.5,
                  lineHeight: 1.5,
                  color: C.ink,
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      data-testid="review-fields"
      data-flush={flush ? "true" : "false"}
      style={{
        // A recess inside the white card. `inset` rather than `canvas`: the
        // page ground reads as a hole punched through the card, where this
        // reads as a surface set into it (design: "Led AI Console").
        //
        // Unless the table IS the card — see `flush`.
        ...(flush
          ? {}
          : { border: `1px solid ${C.hair}`, borderRadius: 12, background: C.inset }),
        overflow: "visible",
      }}
    >
      {fields.map((field, index) => (
        /*
         * 17a stacks the DESCRIPTION and rows everything else.
         *
         * A description is the record's own words — 14px prose that runs to
         * three or four lines. Squeezed into the value half of a label/value
         * row it gets about 60% of the card and wraps five times; stacked, it
         * gets the whole width and wraps twice, and the label above it costs
         * one 12px line. Every other field is a word or two and reads best on
         * one line beside its label, which is what the rest of the table is.
         *
         * The dotted rule under it is the design's, and it is the affordance:
         * with no box and no chevron, it is the only thing saying the prose
         * can be typed into.
         *
         * Flush only. The review card's table is a decision surface read six
         * records at a time, where a uniform row height is what makes it
         * scannable.
         */
        (flush || compact) && field.multiline ? (
          <div
            key={field.label}
            className="fieldrow"
            data-testid="review-field"
            data-field={field.label.toLowerCase()}
            role="group"
            aria-label={field.label}
            aria-invalid={isInvalid(field) || undefined}
            style={{
              background: isInvalid(field) ? C.redSoft : undefined,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: compact ? "9px 12px" : "10px 22px 14px",
              borderTop: index === 0 ? undefined : `1px solid ${C.hairRow}`,
            }}
          >
            <span style={{ fontSize: 13.8, color: C.sub }}>{fieldLabel(field)}</span>
            <span
              style={{
                borderBottom: `1px dotted ${C.line2}`,
                paddingBottom: 3,
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <ReviewFieldControl
                field={field}
                compact={compact}
                value={displayValues[field.key] ?? ""}
                jobSiteId={row.item.jobSiteId}
                category={itemEditCategory(row.item)}
                search={search}
                item={row.item}
                onChange={(value, reference) => update(field, value, reference)}
                onCommit={onCommit}
                isLoadingOptions={false}
              />
              {copyable && (
                <CopyValueButton label={field.label} value={displayValues[field.key] ?? field.value} />
              )}
            </span>
          </div>
        ) : (
        <div
          key={field.label}
          className="fieldrow"
          data-testid="review-field"
          data-field={field.label.toLowerCase()}
          role="group"
          aria-label={field.label}
          aria-invalid={isInvalid(field) || undefined}
          style={{
            background: isInvalid(field) ? C.redSoft : undefined,
            display: "flex",
            /*
             * shadcn's cells are `align-middle`, and for a one-line value
             * that is right: the label and its value sit on one line. A
             * paragraph is the exception — centring a label against three
             * lines of prose floats it in the middle of nothing, so it holds
             * the first line instead.
             */
            alignItems: field.multiline ? "flex-start" : "center",
            gap: compact ? 10 : flush ? 16 : 18,
            // Flush rows take the host table's gutter and rule, so the
            // editable rows and the read-only facts under them read as one
            // list rather than two tables that happen to touch.
            padding: compact ? "9px 12px" : flush ? "12px 22px" : "12px 16px",
            // 17a gives the location row 52px against the others' 46 —
            // the second line needs the room, and only that row has one.
            minHeight:
              flush && field.reference === "location" && locationPath ? 60 : flush ? 54 : 48,
            borderTop:
              index === 0
                ? undefined
                : `1px solid ${flush ? C.hairRow : C.line}`,
          }}
        >
          <span
            style={{
              flexShrink: 0,
              width: compact ? 90 : flush ? 135 : 112,
              fontSize: compact ? 12.65 : 15.52,
              // shadcn's `font-medium text-muted-foreground` for the column
              // that names things — heavy enough to hold the row, light
              // enough that the VALUE is what you read.
              fontWeight: 500,
              color: C.sub,
              letterSpacing: "-0.1px",
              // Optical, not structural: at `flex-start` the label's cap
              // height sits a hair above the first line of prose beside it.
              paddingTop: field.multiline ? 2 : 0,
            }}
          >
            {fieldLabel(field)}
          </span>
          {/*
            17a's location row: the place on top, its ancestry beneath.
        
            `Office 201` alone is ambiguous on a job with four towers, and the
            full string `Tower A › Level 02 › East Wing › Office 201` in one
            line is unreadable at 14px in half a card. The design splits them:
            the leaf is the value, the ancestry is a mono line under it at
            10px — present when it is known, absent when it is not, never a
            placeholder.
        
            Where the ancestry COMES from is the constraint. The record stores
            a resolved reference of `{ family, remoteId, label }` and no path
            (`procoreSync.registry`, `JobSiteManager`), so an observation at
            rest has only its leaf. The project directory has the ancestry —
            `searchIntegrationProjectLocations` returns `nodeName`, which the
            console already maps to `path` — so the line appears once the
            picker has loaded that directory or the reader has chosen a
            location, and a punch item has it at rest because its own
            document carries `locationPath`.
          */}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 2,
            }}
          >
          <ReviewFieldControl
            field={field}
            compact={compact}
            value={displayValues[field.key] ?? ""}
            jobSiteId={row.item.jobSiteId}
            category={itemEditCategory(row.item)}
            search={search}
            item={row.item}
            directoryOptions={
              field.reference === "location"
                ? editOptions?.locations
                : field.reference === "assignee"
                  ? editOptions?.assignees
                  : field.reference === "trade"
                    ? editOptions?.trades
                    : field.reference === "company"
                      ? editOptions?.companies
                      : undefined
            }
            selectedReference={
              field.reference ? references[field.reference] ?? undefined : undefined
            }
            onChange={(value, reference) => update(field, value, reference)}
            onCommit={onCommit}
            onRequestOptions={field.reference && field.reference !== "assignee" ? loadEditOptions : undefined}
            isLoadingOptions={field.reference && field.reference !== "assignee" ? isLoadingEditOptions : false}
            optionsFailed={field.reference && field.reference !== "assignee" ? editOptionsFailed : false}
            failureReason={editOptionsError}
          />
          {field.reference === "location" && locationPath && (
            <span
              data-testid="review-field-path"
              style={{
                fontFamily: FONT_MONO,
                fontSize: 11.5,
                color: C.sub2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {locationPath}
            </span>
          )}
          </span>
          {copyable && (
            <CopyValueButton label={field.label} value={displayValues[field.key] ?? field.value} />
          )}
        </div>
        )
      ))}
    </div>
  );
}

export interface ReviewField {
  key: string;
  label: string;
  value: string;
  rawValue: string;
  calculated?: boolean;
  multiline?: boolean;
  mono?: boolean;
  tone?: "muted" | "warn";
  definition?: ProcoreDailyLogFieldDefinition;
  inputType?: "date" | "number" | "time";
  choices?: readonly string[];
  patchKey?: string;
  reference?: "location" | "assignee" | "trade" | "company";
}

/**
 * The record's fields, from the MOBILE schema where it has one.
 *
 * `mobileRecordFields` reads the phone's own daily-log definitions, which is
 * the whole point: the console must not grow a second field vocabulary that
 * can drift from the app that captured the record. Records outside that
 * schema fall back to what `ConsoleItem` carries, which is what the item list
 * already shows.
 */
/**
 * One property on the review card: the phone's chip — its mark, its name when
 * the mark cannot say it, its value — opening the record page's editor for
 * the field (a list, a tree, people, a calendar, a time). Free text and
 * numbers are typed straight into the pill.
 */
function ReviewPill({
  field, raw, state, item, nameIt, required, requiredLabel, invalid, open, onOpenChange, onCommit, onType,
}: {
  field: ReviewField;
  raw: string;
  state: PropertyState;
  item: ConsoleRow;
  nameIt: boolean;
  required: boolean;
  requiredLabel: string;
  invalid: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit: () => void;
  onType: (value: string) => void;
}) {
  const candidateRequest = useRef(0);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  useEffect(() => () => { candidateRequest.current += 1; },
    [raw, state.references.assignee, state.references.company, state.search, item.id, open]);
  const { glyph, type } = phoneMarkInputs(field);
  const today = useMemo(() => { const at = new Date(); return new Date(at.getFullYear(), at.getMonth(), at.getDate()); }, []);
  // The editor the record page gives this field. A daily log's fixed options
  // (a yes/no, a delay type, its status) are its choices, in its own words.
  // A Procore observation's properties as the record page edits them: the
  // trade directory, the observation's own priorities and statuses, a
  // calendar for its dates (`extendRecordFields` maps those per field and
  // adds nothing for an observation).
  const recordField = isMobileObservation(item) ? extendRecordFields([field], item)[0] ?? field : field;
  const options = recordField.reference || recordField.key === "type" || recordField.choices ? null : selectOptionsFor(recordField, undefined);
  const editorField: ReviewField = {
    ...recordField,
    ...(type === "date" ? { inputType: "date" as const } : type === "time" ? { inputType: "time" as const } : type === "number" ? { inputType: "number" as const } : {}),
    ...(options && !field.choices ? { choices: options.map((option) => option.value) } : {}),
  };
  const labels = options ? new Map(options.map((option) => [option.value, option.label])) : null;
  const picker = !field.calculated && (!!editorField.choices || !!editorField.reference || type === "date" || type === "time" || type === "number" || field.key === "type");
  // No single-key shortcut here: a review card holds several records' pills.
  const spec = { ...specFor(editorField), hotkey: undefined };
  const empty = !raw.trim();
  const showName = nameIt || empty;
  const mark = markFor(glyph, type, field.label);
  const valueMarks = valueMarksFor(glyph, type);

  const name = showName ? (
    <span style={FIELD_NAME_STYLE}>
      {field.label}
      {required && <span data-testid="review-field-required" role="img" aria-label={requiredLabel} title={requiredLabel} style={{ color: C.red, marginLeft: 3 }}>*</span>}
    </span>
  ) : null;
  const markNode = (shown: string) => (
    <Mark
      mark={mark}
      value={phoneMarkValue(field, raw, shown)}
      fieldKey={field.key}
      dateValue={type === "date" ? dateInputValue(raw) || null : undefined}
      // The console's own clock: the phone's record-clock hook runs on the
      // phone's React, not this page's.
      now={today.getTime()}
    />
  );
  const valueText = (shown: string) => (
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: empty ? 500 : 600, color: empty ? RD.quiet : C.ink, fontVariantNumeric: type === "number" ? "tabular-nums" : undefined }}>
      {empty ? "Not set" : shown}
    </span>
  );
  const alarming = mark.kind === "meter" && isAlarmingLevel(raw);
  const dot = alarming ? <span aria-hidden style={{ width: 7, height: 7, borderRadius: 4, flex: "none", background: C.red }} /> : null;

  // A person or a company the walk NAMED (LED-1155): matched reads like any
  // other value; several equally likely people show their faces; a name the
  // directory lacks says so, and opens the picker searching for it.
  const matchRef = field.reference === "assignee" || field.reference === "company" ? field.reference : null;
  const match = matchRef
    ? nameMatchOf({
        value: raw,
        suggestion: matchRef === "assignee" ? item.assigneeSuggestion : item.companySuggestion,
        heardName: matchRef === "assignee" ? item.assigneeHeardName : item.companyHeardName,
        selectedRemoteId: matchRef in state.references ? (state.references[matchRef]?.remoteId ?? "") : undefined,
        directory: matchRef === "assignee" ? item.assigneeDirectory !== false : !!item.syncSource,
      })
    : null;
  const matchPill = match && (match.state === "ambiguous" || match.state === "missing") ? match : null;
  const pickCandidate = async (candidate: NameMatchCandidate) => {
    const request = ++candidateRequest.current;
    setCandidateError(null);
    try {
      const options = matchRef === "assignee"
        ? await fetchItemAssignees(item.jobSiteId, itemEditCategory(item), candidate.name, state.search, item.visitId)
        : (await fetchItemEditOptions(item.jobSiteId, itemEditCategory(item), state.search, item.visitId)).companies;
      if (candidateRequest.current !== request) return;
      const chosen = options.find(option => option.remoteId === candidate.id || option.providerRemoteIds?.includes(candidate.id));
      if (!chosen) throw new Error("Could not confirm this directory match. Open the picker to try again.");
      state.update(field, chosen.name, matchRef === "assignee" ? {
        ...chosen,
        additionalAssignees: (state.references.assignee?.additionalAssignees ?? item.additionalAssignees ?? [])
          .filter(person => person.remoteId !== chosen.remoteId),
      } : chosen);
      onCommit();
    } catch (error) {
      if (candidateRequest.current === request) setCandidateError(error instanceof Error ? error.message : "Could not load the directory. Try again.");
    }
  };

  let body: ReactNode;
  if (field.calculated) {
    body = (
      <span className="review-pill review-pill-static" style={PILL_STYLE} aria-label={`${field.label}: ${empty ? "not set" : raw}`}>
        {markNode(raw)}{name}
        <output data-testid={`review-field-calculated-${field.key}`} style={{ fontWeight: empty ? 500 : 600, color: empty ? RD.quiet : C.ink }}>{empty ? "Not set" : raw}</output>
      </span>
    );
  } else if (picker) {
    body = (
      <Property
        spec={spec}
        state={state}
        item={item}
        today={today}
        open={open}
        onOpenChange={onOpenChange}
        onCommit={onCommit}
        copied={null}
        onCopy={() => {}}
        // Over the page: the next card would otherwise paint over it.
        portal
        labelFor={labels ? (value) => labels.get(value) : undefined}
        optionMark={valueMarks ? (value, label) => valueMarks.mark(phoneMarkValue(editorField, value, label)) : undefined}
        personMark={(person) => <AssigneeAvatar name={person} size={26} />}
        openQuery={matchPill ? nameMatchQuery(matchPill) : undefined}
        renderTrigger={(face) => {
          const shown = face.isSet ? face.shown : "";
          if (matchPill) {
            return (
              <NameMatchPill
                match={matchPill}
                label={field.label}
                // Not the value's initials: the value is a guess here.
                mark={matchRef === "company"
                  ? <Building2 size={12} color={matchPill.state === "missing" ? RV.warnInk : ADR.label} strokeWidth={2} aria-hidden style={{ flex: "none" }} />
                  : <UserRound size={12} color={matchPill.state === "missing" ? RV.warnInk : ADR.label} strokeWidth={2} aria-hidden style={{ flex: "none" }} />}
                triggerRef={face.triggerRef}
                open={face.open}
                onOpen={() => face.toggle()}
                onPick={(candidate) => void pickCandidate(candidate)}
                testId={`review-name-match-${field.key}`}
                buttonTestId={`review-field-editor-${field.key}`}
                style={{ ...PILL_STYLE, cursor: "default" }}
              />
            );
          }
          return (
            <button
              ref={face.triggerRef}
              type="button"
              className="review-pill"
              data-testid={`review-field-editor-${field.key}`}
              aria-haspopup={spec.shape === "calendar" || spec.shape === "number" ? "dialog" : spec.shape === "tree" ? "tree" : "listbox"}
              aria-expanded={face.open}
              aria-label={`${field.label}: ${face.isSet ? shown : "not set"}`}
              title={face.full || field.label}
              onClick={face.toggle}
              style={{ ...PILL_STYLE, ...(face.open ? { borderColor: "rgba(20,17,12,.28)", background: ADR.paper } : {}) }}
            >
              {markNode(shown)}{name}{valueText(shown)}{dot}
            </button>
          );
        }}
      />
    );
  } else {
    body = (
      <label className="review-pill" style={{ ...PILL_STYLE, cursor: "text" }}>
        {markNode(raw)}{name}
        <input
          data-testid={`review-field-editor-${field.key}`}
          aria-label={field.label}
          type="text"
          placeholder={field.definition?.placeholder ?? "Not set"}
          value={raw}
          onChange={(event) => onType(event.target.value)}
          className="review-pill-input"
          style={{ border: 0, outline: "none", background: "transparent", padding: 0, margin: 0, minWidth: "2ch", maxWidth: 360, width: "auto", fieldSizing: "content", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: C.ink } as CSSProperties}
        />
      </label>
    );
  }
  return (
    <div
      data-testid="review-field"
      data-field={field.label.toLowerCase()}
      role="group"
      aria-label={field.label}
      aria-invalid={invalid || undefined}
      style={{ display: "inline-flex", maxWidth: "100%", minWidth: 0 }}
    >
      {body}
      {candidateError && <span role="alert" style={{ color: C.red, fontSize: 12 }}>{candidateError}</span>}
    </div>
  );
}

/** The pill: the phone's chip, at the review card's desktop size. */
const PILL_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 34,
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "0 12px 0 10px",
  borderRadius: 999,
  fontFamily: CHIP_FONT,
  fontSize: 13,
  letterSpacing: "-0.01em",
  color: C.ink,
  whiteSpace: "nowrap",
  cursor: "pointer",
  textAlign: "left",
};

function dateInputValue(value: string | undefined): string {
  return value ? value.slice(0, 10) : "";
}

export function fieldsFor(item: ConsoleRow): ReviewField[] {
  const mobile = mobileRecordFields(item);
  const mobileDefinition = mobileRecordDefinition(item);
  if (mobile) {
    return mobile.map((field) => ({
      key: field.key,
      label: field.label,
      value: field.value,
      rawValue: String(item.fieldValues?.[field.key] ?? ""),
      multiline: field.multiline,
      calculated: field.calculated,
      tone: field.value === "Not set" ? ("muted" as const) : undefined,
      definition: mobileDefinition?.fields.find(
        (definition) => definition.key === field.key,
      ),
      choices: field.key === "status"
        ? [...(item.recordType === "observation" ? OBSERVATION_STATUS_OPTIONS : DAILY_LOG_STATUS_OPTIONS)]
        : undefined,
      reference:
        ((item.category === "manpower" && field.key === "vendor") ||
          (item.category === "inspections" && field.key === "inspectingEntity" && item.syncSource === "procore"))
          ? "company"
          : field.key === "trade"
            ? "trade"
            : field.key === "location"
          ? "location"
          : item.recordType === "observation" && field.key === "assignee"
            ? "assignee"
            : undefined,
    }));
  }

  // The three non-daily-log cards deliberately mirror the phone review
  // stack's per-kind layouts. A generic console metadata table hid the
  // fields people actually review (description, due date and trade) and
  // exposed a few derived values that cannot be written back safely.
  const assignee = item.assigneeName ?? "";
  if (item.kind === "task") {
    const status = normalizeProcoreTaskStatus(item.workflowStatus ?? "initiated");
    // The PHONE's task screen, row for row: Status, Due date, Trade, then
    // Assignee and Company — and no Category, which the user removed
    // (2026-09-09). Two surfaces for one record cannot each decide what a
    // task is made of, and the phone is the one people fill in on site.
    //
    // Description leads because it is the record's own words rather than a
    // field about it; the phone shows the same text as the screen's title.
    return [
      {
        key: "description",
        label: "Description",
        value: item.description || "Not set",
        rawValue: item.description ?? "",
        multiline: true,
        patchKey: "description",
      },
      {
        key: "status",
        label: "Status",
        value: choiceLabel(status),
        rawValue: status,
        choices: PROCORE_TASK_STATUSES,
        patchKey: "status",
      },
      {
        key: "dueDate",
        label: "Due date",
        value: dateInputValue(item.dueDate) || "Not set",
        rawValue: dateInputValue(item.dueDate),
        inputType: "date",
        patchKey: "dueDate",
      },
      {
        key: "trade",
        label: "Trade",
        value: item.trade || "Not set",
        rawValue: item.trade ?? "",
        patchKey: "tradeText",
        reference: "trade",
      },
      {
        key: "assignee",
        label: "Assignee",
        value: assignee || "Not set",
        rawValue: assignee,
        patchKey: "assignee",
        reference: "assignee",
      },
      {
        key: "company",
        label: "Company",
        value: item.assigneeCompany || "Not set",
        rawValue: item.assigneeCompany ?? "",
        reference: "company",
        calculated: true,
      },
    ];
  }
  if (item.kind === "punch") {
    return [
      {
        key: "description",
        label: "Description",
        value: item.description || "Not set",
        rawValue: item.description ?? "",
        multiline: true,
        patchKey: "description",
      },
      {
        key: "priority",
        label: "Priority",
        value: choiceLabel(item.severity ?? "medium"),
        rawValue: item.severity ?? "medium",
        choices: ["low", "medium", "high"],
        patchKey: "priority",
      },
      {
        key: "dueDate",
        label: "Due date",
        value: dateInputValue(item.dueDate) || "Not set",
        rawValue: dateInputValue(item.dueDate),
        inputType: "date",
        patchKey: "dueDate",
      },
      {
        key: "location",
        label: "Location",
        value: item.location?.name ?? "Not set",
        rawValue: item.location?.name ?? "",
        patchKey: "locationText",
        reference: "location",
      },
      {
        key: "assignee",
        label: "Assignee",
        value: assignee || "Not set",
        rawValue: assignee,
        patchKey: "assignee",
        reference: "assignee",
      },
      {
        key: "trade",
        label: "Trade",
        value: item.trade || "Not set",
        rawValue: item.trade ?? "",
        patchKey: "tradeText",
        reference: "trade",
      },
    ];
  }
  if (item.kind === "observation") {
    return [
      {
        key: "summary",
        label: "Summary",
        value: item.summary || "Not set",
        rawValue: item.summary ?? "",
        multiline: true,
        patchKey: "summary",
      },
    ];
  }
  return [];
}

/** The pills a review card shows per kind, in the design's order. */
export const REVIEW_CARD_PILLS: Partial<Record<ConsoleRow["kind"], readonly string[]>> = {
  task: ["assignee", "dueDate", "priority"],
  punch: ["priority", "dueDate", "location", "assignee"],
};

/**
 * The review card's fields (design "Review card", LED-1153): the record's
 * prose first, then the kind's pills in the design's order. A task's
 * Priority is not in its record-page set, so it is added here — the task
 * review RPC takes `priority`. Kinds without a listed set keep every field.
 */
export function reviewCardFields(item: ConsoleRow, fields: ReviewField[]): ReviewField[] {
  const keys = REVIEW_CARD_PILLS[item.kind];
  if (!keys) return fields;
  const byKey = new Map(fields.map((field) => [field.key, field]));
  if (item.kind === "task" && !byKey.has("priority")) {
    byKey.set("priority", {
      key: "priority",
      label: "Priority",
      value: item.severity ? choiceLabel(item.severity) : "Not set",
      rawValue: item.severity ?? "",
      choices: ["low", "medium", "high"],
      patchKey: "priority",
    });
  }
  return [
    ...fields.filter((field) => field.multiline),
    ...keys.map((key) => byKey.get(key)).filter((field): field is ReviewField => !!field && !field.multiline),
  ];
}

function selectOptionsFor(
  field: ReviewField,
  directoryOptions: ItemEditDirectoryOption[] | undefined,
): Array<{
  value: string;
  label: string;
  directory?: ItemEditDirectoryOption;
}> | null {
  if (field.reference) {
    return (directoryOptions ?? []).map((option) => ({
      value: option.remoteId,
      label: [option.name, option.company ?? option.path]
        .filter(Boolean)
        .join(" · "),
      directory: option,
    }));
  }
  if (field.choices) {
    return field.choices.map((value) => ({ value, label: choiceLabel(value) }));
  }
  if (field.definition?.type === "boolean") {
    return [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ];
  }
  if (field.key === "priority") {
    const values = field.definition
      ? OBSERVATION_PRIORITY_OPTIONS
      : ["low", "medium", "high"];
    return values.map((value) => ({
      value,
      label: value.replace(/\b\w/g, (letter) => letter.toUpperCase()),
    }));
  }
  if (field.key === "status") {
    const values = field.definition
      ? field.definition.key === "status" &&
        field.label.toLowerCase().includes("status")
        ? OBSERVATION_STATUS_OPTIONS
        : DAILY_LOG_STATUS_OPTIONS
      : ["open", "in_progress", "closed"];
    return values.map((value) => ({
      value,
      label: choiceLabel(value),
    }));
  }
  if (field.key === "delayType") {
    return PROCORE_DELAY_TYPE_OPTIONS.map((value) => ({ value, label: value }));
  }
  if (field.key === "type" && field.definition) {
    return DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS.map((value) => ({
      value,
      label: value,
    }));
  }
  return null;
}

function valueForPatch(
  definition: ProcoreDailyLogFieldDefinition,
  value: string,
): ProcoreDailyLogFieldValue {
  if (value.trim() === "") return null;
  if (definition.type === "boolean") return value === "true";
  if (definition.type === "number") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return value;
}

export function buildReviewPatch(
  item: ConsoleRow,
  fields: ReviewField[],
  values: Record<string, string>,
  references: Partial<Record<"location" | "assignee" | "trade" | "company", ItemEditDirectoryOption | null>>,
  mobileFields: readonly ProcoreDailyLogFieldDefinition[] | undefined,
): Record<string, unknown> {
  if (mobileFields) {
    const patch: Record<string, unknown> = {
      fieldValues: Object.fromEntries(
        mobileFields.filter((field) => !fields.some((entry) => entry.key === field.key && entry.calculated)).map((field) => [
          field.key,
          valueForPatch(field, values[field.key] ?? ""),
        ]),
      ),
    };
    if ("location" in references) {
      patch.selectedLocationRemoteId = references.location?.remoteId ?? null;
      patch.selectedLocationName = references.location?.name ?? null;
    }
    if ("assignee" in references) {
      patch.selectedAssigneeRemoteId = references.assignee?.remoteId ?? null;
      patch.selectedAssigneeName = references.assignee?.name ?? null;
      patch.additionalAssignees = references.assignee?.additionalAssignees ?? [];
    }
    if ("trade" in references) {
      patch.selectedTradeRemoteId = references.trade?.remoteId ?? null;
      patch.selectedTradeName = references.trade?.name ?? null;
    }
    if ("company" in references) {
      patch.selectedCompanyRemoteId = references.company?.remoteId ?? null;
      patch.selectedCompanyName = references.company?.name ?? null;
    }
    return patch;
  }

  const patch: Record<string, unknown> = Object.fromEntries(
    fields
      .filter((field) => field.patchKey)
      .map((field) => [field.patchKey!, values[field.key] || null]),
  );
  if ("location" in references) {
    patch.selectedLocationRemoteId = references.location?.remoteId ?? null;
    patch.selectedLocationName = references.location?.name ?? null;
  }
  if ("assignee" in references) {
    patch.selectedAssigneeRemoteId = references.assignee?.remoteId ?? null;
    patch.selectedAssigneeName = references.assignee?.name ?? null;
    patch.additionalAssignees = references.assignee?.additionalAssignees ?? [];
  }
  if ("trade" in references) {
    patch.selectedTradeRemoteId = references.trade?.remoteId ?? null;
    patch.selectedTradeName = references.trade?.name ?? null;
  }
  if (item.kind !== "task" && "company" in references) {
    patch.selectedCompanyRemoteId = references.company?.remoteId ?? null;
    patch.selectedCompanyName = references.company?.name ?? null;
  }
  return patch;
}

export function choiceLabel(value: string): string {
  if (value === "voided") return "Void";
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * A Procore location, split into the place and the way to it.
 *
 * Procore names a location by its whole path, `>`-separated and unspaced:
 * `Tower A>Level 02>East Wing`. That is what the record stores and what the
 * directory returns as an option's `name`, so rendering it as the value put
 * a truncated path where 17a puts a room — `Tower A>Level 02>East W…` —
 * which is the part of the string that says least.
 *
 * The last segment is the place; the rest is how you get there. Same split
 * `buildLocationNodeName` makes on the backend, which is what makes the leaf
 * here the same leaf Procore calls the node.
 *
 * NOTE for anyone reaching for `ItemEditDirectoryOption.path`: that holds
 * the backend's `nodeName`, which is the LEAF, not the ancestry. A version
 * of this row shipped reading it as the second line and printed the room
 * twice.
 */
export function splitLocation(value: string | undefined): {
  place: string;
  ancestry: string | undefined;
} {
  const segments = (value ?? "")
    .split(">")
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) return { place: value?.trim() ?? "", ancestry: undefined };
  return {
    place: segments.at(-1)!,
    ancestry: segments.length > 1 ? segments.slice(0, -1).join(" › ") : undefined,
  };
}

function ReviewCombobox({
  fieldKey,
  compact = false,
  label,
  value,
  options,
  onChange,
  onRequestOptions,
  isLoading,
  optionsFailed,
  failureReason,
  emptyText,
  onCreate,
  pill = false,
}: {
  fieldKey: string;
  compact?: boolean;
  /** Inside a review field pill: left-aligned, no indicator, the whole
   *  location path rather than its leaf. */
  pill?: boolean;
  label: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
    directory?: ItemEditDirectoryOption;
  }>;
  onChange: (
    option: {
      value: string;
      label: string;
      directory?: ItemEditDirectoryOption;
    } | null,
  ) => void;
  onRequestOptions?: () => void;
  isLoading: boolean;
  /** The directory read FAILED, as opposed to returning nothing. The panel
   *  says so — see the empty state below. */
  optionsFailed?: boolean;
  /** The backend's own sentence for WHY, when it gave one. */
  failureReason?: string;
  /** What the panel says with nothing to list and nothing typed. */
  emptyText?: string;
  /** Offer to USE what was typed when no option matches it — a free-text
   *  field wearing the combobox, so it reads like its neighbours. */
  onCreate?: (value: string) => void;
}) {
  const current = options.find(option => option.value === value);
  // Location labels carry the full hierarchy; their directory detail is
  // the leaf again. Split the name before adding picker-only detail.
  const displayLabel = fieldKey === "location" ? current?.directory?.name ?? current?.label : current?.label;
  const located = splitLocation(displayLabel ?? (value ? choiceLabel(value) : ""));
  const displayValue = (pill && located.ancestry ? `${located.ancestry} › ${located.place}` : located.place) || "Not set";
  return <FieldCombobox
    label={label} value={current?.value ?? value} options={options}
    onChange={next => onChange(options.find(option => option.value === next) ?? null)}
    onRequestOptions={onRequestOptions} loading={isLoading}
    error={optionsFailed ? failureReason ?? "Could not load the project directory." : undefined}
    emptyText={emptyText} onCreate={onCreate}
    clearLabel="Not set" align={compact || pill ? "start" : "end"} showIndicator={!pill}
    triggerTestId={`review-field-editor-${fieldKey}`} panelTestId={`review-field-panel-${fieldKey}`}
    inputTestId={`review-field-search-${fieldKey}`} emptyTestId={`review-field-empty-${fieldKey}`}
    triggerStyle={pill
      ? { width: "auto", justifyContent: "flex-start", textAlign: "left", padding: 0, fontSize: 14, fontWeight: value ? 600 : 500, lineHeight: 1.3, color: value ? C.ink : C.sub2 }
      : { justifyContent: compact ? "space-between" : "flex-end", textAlign: compact ? "left" : "right", color: value ? C.ink : C.sub2 }}
  >
    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{displayValue}</span>
  </FieldCombobox>;
}

/** `"2026-09-01"` → `"Sep 1, 2026"`, the design's own date voice. */
function displayDate(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const at = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(at.getTime())) return null;
  return `${MONTHS[at.getMonth()]} ${at.getDate()}, ${at.getFullYear()}`;
}

/**
 * A date, wearing the ASSIGNEE pill, opening the console's OWN calendar.
 *
 * Two complaints, one field. The block it used to sit in filled the whole row
 * while Assignee — the same job, one row down — was a chip that hugged its
 * value. And the control it opened was Chrome's: a grey-and-blue dialog with
 * its own type, dropped on a warm console, looking like another application's
 * window (user, 2026-09-10).
 *
 * So: the assignee's pill, and `Calendar` — the console's one month grid, in
 * shadcn's geometry and this console's palette.
 *
 * ## What keeping the native input was buying, and how it is paid for now
 *
 * Timezone, locale and keyboard correctness. None of it is reimplemented:
 * every date crossing this boundary is a `YYYY-MM-DD` built from LOCAL
 * calendar parts, and the grid is plain buttons, so focus order and
 * Enter/Space are the browser's own. What is gone is only the chrome.
 */
function ReviewDateField({
  fieldKey,
  label,
  value,
  onChange,
  pill = false,
}: {
  fieldKey: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Inside a review field pill: the date as words, nothing around it. */
  pill?: boolean;
}) {
  const shown = displayDate(value);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  // Read once per open rather than per render: a calendar that recomputed
  // "today" on every keystroke could move its own highlight mid-interaction.
  const today = useMemo(() => new Date(), [open]);

  const panel = open && (
    <AnchoredPanel
      anchorRef={anchorRef}
      role="dialog"
      label={label}
      align={pill ? "left" : "right"}
      testId={`review-field-calendar-${fieldKey}`}
      minWidth={268}
      maxWidth={300}
      panelStyle={{
        padding: 0,
        border: `1px solid ${C.popLine}`,
        borderRadius: 14,
        boxShadow: `0 1px 2px ${C.hairRow}, 0 14px 36px ${C.menuShadow}`,
      }}
      onClose={() => setOpen(false)}
    >
      <Calendar
        value={dateInputValue(value)}
        today={today}
        testId={`review-calendar-${fieldKey}`}
        onSelect={(next) => {
          onChange(next);
          setOpen(false);
        }}
      />
    </AnchoredPanel>
  );

  if (pill) {
    return (
      <>
        <button
          ref={anchorRef}
          type="button"
          data-testid={`review-field-editor-${fieldKey}`}
          aria-label={shown ? `Change ${label}` : label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          style={{ border: "none", background: "transparent", padding: 0, font: "inherit", fontSize: 14, fontWeight: shown ? 600 : 500, color: shown ? C.ink : C.sub2, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          {shown ?? "Not set"}
        </button>
        {panel}
      </>
    );
  }

  if (!shown) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end" }}>
        <button
          ref={anchorRef}
          type="button"
          data-testid={`review-field-editor-${fieldKey}`}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            padding: "5px 18px 5px 7px",
            color: C.sub2,
            textAlign: "right",
            font: "inherit",
            fontSize: 16.67,
            fontWeight: 650,
            cursor: "pointer",
          }}
        >
          Set a date
        </button>
        {panel}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          minWidth: 0,
          maxWidth: "100%",
          border: `1px solid ${C.line2}`,
          borderRadius: 999,
          padding: "3px 5px 3px 4px",
          background: C.faint,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.42)",
        }}
      >
        <button
          ref={anchorRef}
          type="button"
          data-testid={`review-field-editor-${fieldKey}`}
          aria-label={`Change ${label}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            minWidth: 0,
            gap: 7,
            border: "none",
            background: "transparent",
            padding: "0 5px 0 0",
            color: C.ink,
            font: "inherit",
            fontSize: 16.1,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 23,
              height: 23,
              flex: "none",
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              background: C.ink,
              color: C.surface,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <rect x="1.7" y="2.8" width="10.6" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.3" />
              <path d="M1.7 5.9h10.6M4.6 1.5v2.2M9.4 1.5v2.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {shown}
          </span>
        </button>
        <button
          type="button"
          className="kb"
          aria-label={`Clear ${label}`}
          data-testid={`review-field-clear-${fieldKey}`}
          onClick={() => {
            onChange("");
            setOpen(false);
          }}
          style={{
            width: 24,
            height: 24,
            flex: "none",
            border: "none",
            borderRadius: "50%",
            background: C.line2,
            color: C.sub,
            display: "grid",
            placeItems: "center",
            padding: 0,
            cursor: "pointer",
            fontSize: 20.7,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </span>
      {panel}
    </div>
  );
}



/**
 * A description field that is exactly as tall as its text.
 *
 * The old one was a fixed 64px box with `resize: vertical`, which showed the
 * browser's own corner grabber — a diagonal scratch in the bottom-right of
 * the row, and the thing that made the description look like a form control
 * dropped into a table of facts. It also meant a long description scrolled
 * inside a two-line window while the row around it stayed empty.
 *
 * Measuring in a layout effect rather than on input: the height has to be
 * right in the frame the value changes, or the row jumps a frame after the
 * keystroke. `height = auto` first, because `scrollHeight` on an element
 * that is already tall enough reports the height it currently has.
 */
function AutoTextarea({
  label,
  testId,
  value,
  onChange,
  style,
  className,
  placeholder,
}: {
  label: string;
  testId: string;
  value: string;
  onChange: (next: string) => void;
  style: CSSProperties;
  /** `review-editable`: the design's hover and focus ground on inline text. */
  className?: string;
  /** Overrides `Add {label}…`. */
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const resize = () => {
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    };
    resize();
    // The split pane changes width when the window or navigation resizes.
    let width = node.getBoundingClientRect().width;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      const next = node.getBoundingClientRect().width;
      if (next !== width) { width = next; resize(); }
    });
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [value]);
  return (
    <textarea
      ref={ref}
      aria-label={label}
      placeholder={placeholder ?? `Add ${label.toLowerCase()}…`}
      data-testid={testId}
      className={className}
      rows={1}
      value={value}
      onInput={(event) => onChange(event.currentTarget.value)}
      onChange={(event) => onChange(event.target.value)}
      style={{
        ...style,
        display: "block",
        minHeight: 0,
        padding: className ? "2px 6px" : 0,
        // It sizes itself; a scrollbar or a grabber would both be saying the
        // reader has to do something about a box that is already the right
        // height.
        resize: "none",
        overflow: "hidden",
      }}
    />
  );
}

function ReviewFieldControl({
  field,
  compact = false,
  value,
  jobSiteId,
  category,
  search,
  item,
  directoryOptions,
  selectedReference,
  onChange,
  onRequestOptions,
  isLoadingOptions,
  optionsFailed,
  failureReason,
  onCommit,
  pill = false,
}: {
  field: ReviewField;
  compact?: boolean;
  /** Inside a review field pill (`FieldRows` `layout="pills"`). */
  pill?: boolean;
  value: string;
  jobSiteId: string;
  category: string | undefined;
  search: string;
  /** The record being reviewed — read only for the assignee row, which shows
   *  the match behind the name and needs the kind for its modal's eyebrow. */
  item: ConsoleRow;
  directoryOptions?: ItemEditDirectoryOption[];
  selectedReference?: ItemEditDirectoryOption;
  onChange: (value: string, reference?: ItemEditDirectoryOption) => void;
  onRequestOptions?: () => void;
  isLoadingOptions: boolean;
  optionsFailed?: boolean;
  /** The backend's own sentence for a directory that did not load. */
  failureReason?: string;
  onCommit?: () => void;
}) {
  if (field.reference === "assignee") {
    return (
      <AssigneePicker
        jobSiteId={jobSiteId}
        visitId={item.visitId}
        category={category}
        search={search}
        label={field.label}
        value={value}
        suggestion={item.assigneeSuggestion}
        directoryEnabled={item.assigneeDirectory}
        multiple={item.syncSource === "procore" && (item.kind === "task" || item.kind === "punch" || item.category === "observations")}
        additionalAssignees={selectedReference?.additionalAssignees ?? item.additionalAssignees}
        // A pick made a moment ago is not on the record yet — see the prop.
        selectedRemoteId={selectedReference?.remoteId}
        // An observation is a daily-log item, so `kind` alone would head the
        // sheet "DAILY LOG · ASSIGNEE" on the one record type that most often
        // has an assignee at all. The card's own chip already names it the
        // way the phone does; this reads the same source.
        typeLabel={mobileRecordKindLabel(item) ?? KIND_LABEL[item.kind]}
        onChange={(next, reference) => {
          onChange(next, reference);
          onCommit?.();
        }}
        testId={`review-field-editor-${field.key}`}
        {...(pill ? {
          variant: "property" as const,
          align: "start" as const,
          showAvatar: false,
          valueStyle: { padding: 0, fontSize: 14, fontWeight: value ? 600 : 500, color: value ? C.ink : C.sub2, background: "transparent" },
        } : {})}
      />
    );
  }
  // Auto-fill can select a company/trade before its picker has ever loaded.
  // Retain the chosen label even when it is outside the first directory page.
  const choices = selectedReference?.remoteId && !directoryOptions?.some((option) => option.remoteId === selectedReference.remoteId)
    ? [...(directoryOptions ?? []), selectedReference]
    : directoryOptions;
  const options = selectOptionsFor(field, choices);
  // The design's per-state value type: a body field reads lighter and looser
  // than a set value, an EMPTY one is muted (it is a prompt, not a fact), and
  // a mono field — a location, a reference — is uppercase with the tracking
  // the console's mono voice uses everywhere else.
  const style: CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    border: "none",
    outline: "none",
    background: "transparent",
    /*
     * The description reads in INK, like every other value.
     *
     * It was the one field set in the muted `sub` — on the argument that a
     * body field is quieter than a set value. That is backwards on this
     * page: the description is the record's own words, the longest thing in
     * the table and usually the reason somebody opened it, and it was the
     * only row greyed out. 17a sets it at `rgba(10,10,10,.84)`, which is
     * this palette's ink, not its muted tone.
     *
     * An EMPTY value stays muted, which is the distinction that was worth
     * drawing all along: a prompt is quieter than a fact.
     */
    color: value ? C.ink : C.sub2,
    /*
     * A one-line value is ranged RIGHT, against the label at the left — the
     * console's property tables have always read that way, and the read-only
     * facts directly under this table still do, so the two halves have to
     * agree.
     *
     * A PARAGRAPH is ranged left. Right-aligned prose gives every line a
     * different starting point, so the eye has to hunt for the next one; on
     * a three-line description that is the difference between reading it and
     * looking at it. This is the one place the shadcn table's `text-left` is
     * simply right, and the one place the old table was worst.
     */
    textAlign: field.multiline || compact ? "left" : "right",
    /*
     * The family is named, not inherited.
     *
     * `font: inherit` is a SHORTHAND, and a `<textarea>` starts from the
     * UA's `monospace` — so a description rendered in the browser's
     * typewriter face any time the shorthand did not fully take. The Home
     * command bar was bitten by the same shorthand resetting `font-size`;
     * naming the property removes the class of bug rather than one instance.
     */
    fontFamily: field.mono ? FONT_MONO : FONT_TEXT,
    fontSize: field.multiline ? 16.1 : 16.67,
    fontWeight: field.multiline ? 400 : 600,
    lineHeight: field.multiline ? 1.55 : 1.45,
    letterSpacing: field.mono ? "0.07em" : "-0.15px",
    textTransform: field.mono ? "uppercase" : undefined,
    ...(pill ? {
      width: "auto",
      minWidth: "3ch",
      maxWidth: "100%",
      textAlign: "left",
      fontSize: 14,
      fontWeight: value ? 600 : 500,
      lineHeight: 1.3,
      fieldSizing: "content",
    } : {}),
  } as CSSProperties;

  if (field.calculated) {
    return <output aria-label={field.label} data-testid={`review-field-calculated-${field.key}`} style={style}>
      {value || "Not set"}
    </output>;
  }

  if (options) {
    const selectValue = selectedReference?.remoteId ?? value;
    const choose = (option: typeof options[number] | null) => {
      onChange(option?.directory?.name ?? option?.value ?? "", option?.directory);
      onCommit?.();
    };
    // A trade on a project with no directory is TYPED, the way the assignee
    // is there (LED-529): the same combobox, offering to use what was typed.
    // `assigneeDirectory` is the row's "this site has a Procore or Autodesk
    // directory" flag — named for the field that first needed it, but it is
    // a fact about the site, and the trade directory comes from the same
    // link. There is nothing to load, so nothing is requested and no
    // directory failure can show. Passing no reference makes the patch clear
    // any stale directory identity (`selectedTradeRemoteId: null`) alongside
    // the typed `tradeText`.
    const typedTrade = field.reference === "trade" && !item.assigneeDirectory;
    const typedInspectionCompany = field.reference === "company" && item.category === "inspections" && field.key === "inspectingEntity";
    return (
      <ReviewCombobox
        compact={compact}
        pill={pill}
        fieldKey={field.key}
        label={field.label}
        value={selectValue}
        options={options}
        onRequestOptions={field.reference && !typedTrade ? onRequestOptions : undefined}
        isLoading={!!field.reference && !typedTrade && isLoadingOptions}
        optionsFailed={!!field.reference && !typedTrade && optionsFailed}
        failureReason={failureReason}
        emptyText={typedTrade ? "Type a trade to set it." : undefined}
        onCreate={typedTrade || typedInspectionCompany ? (name) => { onChange(name); onCommit?.(); } : undefined}
        onChange={choose}
      />
    );
  }

  if (field.inputType === "time" || field.definition?.type === "time") {
    return (
      <TimeField
        fieldKey={field.key}
        label={field.label}
        value={value}
        onChange={onChange}
        testId={`review-field-editor-${field.key}`}
        pill={pill}
      />
    );
  }

  if (field.inputType === "date" || field.definition?.type === "date") {
    return (
      <ReviewDateField
        fieldKey={field.key}
        label={field.label}
        value={value}
        onChange={onChange}
        pill={pill}
      />
    );
  }

  if (field.multiline) {
    return (
      <AutoTextarea
        label={field.label}
        testId={`review-field-editor-${field.key}`}
        value={value}
        onChange={onChange}
        style={style}
      />
    );
  }

  return (
    <input
      aria-label={field.label}
      placeholder={field.definition?.placeholder ?? "Not set"}
      data-testid={`review-field-editor-${field.key}`}
      // Neither `time` nor `date` reaches this input: both are controls of
      // our own above, because the browser's own are three blue spinner
      // columns and a calendar in nobody's design system.
      type={field.definition?.type === "number" || field.inputType === "number" ? "number" : "text"}
      step={field.definition?.type === "number" || field.inputType === "number" ? "any" : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={style}
    />
  );
}

function RecordCard({
  row,
  search,
  configured,
  note,
  onDecided,
  autodeskDestinations,
  onConverted,
}: {
  row: ReviewRowResponse;
  search: string;
  configured: boolean;
  note: string;
  /** After a decision LANDS: the page folds the card into its decided row
   *  and re-reads the queue. */
  onDecided: (decision: ReviewDecision, startedAt: number) => void;
  /** Autodesk projects only: the daily-report sections this record can fill. */
  autodeskDestinations?: AutodeskReviewDestinations;
  onConverted?: () => void;
}) {
  const item = row.item;
  const [recordPhotos, setRecordPhotos] = useState(row.photos);
  useEffect(() => setRecordPhotos(row.photos), [row.photos]);
  // Made from a walk photo (LED-1154): that photo is in the origin strip.
  const origin = useWalkPhotoOrigin(item.id);
  const photos = withoutOriginPhoto(recordPhotos, origin);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [mediaRefreshFailed, setMediaRefreshFailed] = useState(false);
  const mediaPending = useRef(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const mediaPath = `/api/items/${row.write.kind}/${encodeURIComponent(row.write.itemId)}/media`;
  async function changeMedia({ files, removeId, mediaIds }: { files?: File[]; removeId?: string; mediaIds?: string[] }) {
    if (!configured || decisionBusy || mediaPending.current) return false;
    mediaPending.current = true;
    setMediaBusy(true);
    setMediaError(null);
    let problem: string | null = null;
    try {
      if (removeId) {
        const result = await deleteMutation(`${mediaPath}/${encodeURIComponent(removeId)}`, search);
        if (!result.ok) throw new Error(result.error || "Could not remove photo.");
      } else if (mediaIds) {
        for (const mediaId of mediaIds) {
          const result = await postMutation(mediaPath, search, { mediaId });
          if (!result.ok) throw new Error(result.error || "Could not attach photo.");
        }
      } else {
        for (const file of files ?? []) {
          const payload = await readRecordPhoto(file);
          const result = await postMutation(mediaPath, search, payload);
          if (!result.ok) throw new Error(result.error || `Could not upload ${file.name}.`);
        }
      }
    } catch (error) { problem = error instanceof Error ? error.message : "Could not update photos."; }
    // Also refresh after a partial multi-file failure so landed uploads stay visible.
    try {
      const detail = await fetchItemDetail(item.id, search);
      setRecordPhotos(detail.evidence);
      setMediaRefreshFailed(false);
      setPhotoViewer(null);
    } catch {
      setMediaRefreshFailed(true);
      problem = [problem, "Could not refresh photos. Reload before continuing review."].filter(Boolean).join(" ");
    }
    setMediaError(problem);
    mediaPending.current = false;
    setMediaBusy(false);
    return !problem;
  }
  const viewerPhotos: FullscreenPhoto[] = photos.map((photo) => ({
    ...photo,
    walkName: row.walkName,
    siteName: item.jobSiteId,
  }));
  const [photoViewer, setPhotoViewer] = useState<number | null>(null);
  const [titleState, setTitleState] = useState(item.title);
  const punchFields = fieldsFor(item);
  const punchRemote = { ...buildReviewPatch(item, punchFields,
    Object.fromEntries(punchFields.map(field => [field.key, field.rawValue])), {}, undefined), title: item.title,
    selectedAssigneeRemoteId: item.assigneeSuggestion?.selectedRemoteId ?? item.assigneeSuggestion?.selected?.remoteId ?? null,
    selectedAssigneeName: item.assigneeName ?? null,
    additionalAssignees: item.additionalAssignees ?? [],
  };
  const punchSave = useLiveReviewDraft(item.id, punchRemote, async (patch) => {
    if (!configured) throw new Error("Record editing is unavailable.");
    const result = await postItemEdit(item.kind, item.id, search, patch);
    if (!result.ok) throw new Error(result.error || "Could not save punch item.");
  });
  const title = item.kind === "punch" ? String(punchSave.value.title) : titleState;
  const setTitle = (value: string) => item.kind === "punch"
    ? punchSave.change({ ...punchSave.value, title: value }) : setTitleState(value);
  const [fieldPatch, setFieldPatchState] = useState<Record<string, unknown>>({});
  const setFieldPatch = (patch: Record<string, unknown>) => {
    if (item.kind === "punch") punchSave.change({ ...punchSave.value, ...patch });
    else setFieldPatchState(patch);
  };
  const [validationFieldKeys, setValidationFieldKeys] = useState<string[]>([]);
  // A manpower entry colliding with a row on today's Procore log (LED-1156):
  // the reviewer's choice rides Accept as the phone's own merge keys.
  const merge = item.category === "manpower" ? item.manpowerMerge : undefined;
  const mergeTarget = merge
    ? merge.candidates.find((candidate) => candidate.remoteId === merge.targetRemoteId) ?? merge.candidates[0]
    : undefined;
  const [mergeChoice, setMergeChoice] = useState<ManpowerMergeResolution | null>(null);
  const conflict = merge && mergeTarget ? {
    variant: merge.variant,
    choice: manpowerConflictChoice(merge.variant, mergeChoice ?? merge.resolution, merge.suggested),
    existing: {
      company: mergeTarget.companyLabel ?? String(item.fieldValues?.vendor ?? ""),
      workers: mergeTarget.workers,
      hours: mergeTarget.hours,
      location: mergeTarget.locationLabel,
    },
    walk: {
      company: String(item.fieldValues?.vendor ?? mergeTarget.companyLabel ?? ""),
      workers: readManpowerCount(item.fieldValues?.workers),
      hours: readManpowerCount(item.fieldValues?.hours),
      location: item.location?.name ?? (typeof item.fieldValues?.location === "string" ? item.fieldValues.location : null),
    },
  } : null;
  const decisionPatch = {
    ...fieldPatch,
    ...(item.kind !== "punch" && title.trim() !== item.title ? { title: title.trim() } : {}),
    ...(conflict && mergeTarget
      ? manpowerConflictSubmission({
          choice: conflict.choice,
          targetRemoteId: mergeTarget.remoteId,
          existingWorkers: mergeTarget.workers,
          walkWorkers: conflict.walk.workers,
          fieldValues: {
            ...(item.fieldValues ?? {}),
            ...((fieldPatch.fieldValues as Record<string, unknown> | undefined) ?? {}),
          },
        })
      : {}),
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const focused = new URLSearchParams(search).get("reviewItem") === item.id;
  useLayoutEffect(() => { if (focused) cardRef.current?.scrollIntoView?.({ block: "start" }); }, [focused]);
  const images = photos.length;

  return (
    <div
      ref={cardRef}
      data-testid="review-row"
      data-item-id={item.id}
      className={origin?.fresh ? `review-fade ${WALK_PHOTO_FRESH_CLASS}` : "review-fade"}
      style={{
        background: ADR.paper,
        borderRadius: 18,
        boxShadow: "0 1px 0 rgba(20,17,12,.04),0 10px 28px rgba(20,17,12,.06)",
        // NOT `hidden`: the kind menu and the field pickers are positioned
        // against this card and have to escape it.
        overflow: "visible",
      }}
    >
      <div style={{ padding: "16px 18px 0" }}>
        {origin && <WalkPhotoOriginTag origin={origin} />}
        {/* The kind — and the move to another — and when it was generated. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          {autodeskDestinations ? (
            <AutodeskKindChip
              item={item}
              destinations={autodeskDestinations}
              search={search}
              disabled={!configured || decisionBusy}
              onConverted={() => onConverted?.()}
            />
          ) : (
            <ProcoreKindChip
              item={item}
              search={search}
              disabled={!configured || decisionBusy}
              onConverted={() => onConverted?.()}
            />
          )}
          <span style={{ ...META, flex: "none", fontSize: 9, fontWeight: 600, letterSpacing: "1.2px", color: ADR.label }}>
            {whenStamp(item.createdAt)}
          </span>
        </div>

        <AutoTextarea
          testId="row-title"
          label="Title"
          value={title}
          onChange={setTitle}
          className="review-editable"
          style={{
            width: "calc(100% + 12px)",
            boxSizing: "border-box",
            margin: "10px -6px 0",
            border: "none",
            outline: "none",
            borderRadius: 6,
            fontFamily: FONT_TEXT,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "-0.5px",
            lineHeight: 1.2,
            color: C.ink,
            overflowWrap: "anywhere",
          }}
        />

        {conflict ? (
          <div style={{ marginTop: 12 }}>
            <ManpowerConflictPanel
              company={conflict.walk.company || conflict.existing.company}
              existing={conflict.existing}
              walk={conflict.walk}
              variant={conflict.variant}
              choice={conflict.choice}
              onChoice={setMergeChoice}
              disabled={decisionBusy}
              testId="review-manpower-conflict"
            />
            <div style={{ marginTop: 12 }}>
              <FieldRows
                layout="pills"
                proseOnly
                item={row.item}
                search={search}
                onPatchChange={setFieldPatch}
                validationFieldKeys={validationFieldKeys}
              />
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <FieldRows
              layout="pills"
              reviewSet
              item={row.item}
              liveDraft={item.kind === "punch" ? punchSave.value : undefined}
              search={search}
              onPatchChange={setFieldPatch}
              validationFieldKeys={validationFieldKeys}
            />
          </div>
        )}
        {origin && <WalkPhotoOriginStrip origin={origin} disabled={!configured || decisionBusy} />}

        {item.category === "manpower" && (() => {
          const values = (fieldPatch.fieldValues ?? item.fieldValues ?? {}) as Record<string, unknown>;
          return values.workers === null || values.workers === undefined || values.workers === "";
        })() ? <p role="status" style={{ marginTop: 12, color: ADR.label }}>No headcount given. Enter the number of workers.</p> : null}

        {/* ---- the evidence ---- */}
        {images > 0 ? (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 18 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, letterSpacing: "1.4px", color: ADR.label, textTransform: "uppercase" }}>
                <CameraGlyph />
                {`Media · ${images} ${images === 1 ? "photo" : "photos"}`}
              </span>
              <button
                type="button"
                className="kb review-outline"
                data-testid="review-add-photos"
                aria-haspopup="dialog"
                aria-expanded={photoPickerOpen}
                disabled={!configured || mediaBusy || decisionBusy}
                onClick={() => { setMediaError(null); setPhotoPickerOpen(true); }}
                style={{ height: 30, padding: "0 12px", borderRadius: 999, border: "1px solid rgba(20,17,12,.12)", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: C.ink, cursor: "pointer", fontFamily: "inherit" }}
              >
                <Plus size={13} aria-hidden />
                {mediaBusy ? "Updating…" : "Add"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              {photos.map((photo, index) => (
                <div key={photo.id} className="review-tile" style={{ position: "relative", width: 150, height: 150, borderRadius: 14, overflow: "hidden", background: ADR.tile }}>
                  <a
                    className="kb"
                    data-testid="review-photo"
                    href={photo.url ?? photo.thumbnailUrl ?? undefined}
                    onClick={(event) => { event.preventDefault(); setPhotoViewer(index); }}
                    aria-label={`Photo ${photoStamp(photo.capturedAt)}`}
                    style={{ display: "block", width: 150, height: 150 }}
                  >
                    <EvidenceTile jobSiteId={item.jobSiteId} url={photo.thumbnailUrl ?? photo.url} height={150} alt={photo.description ?? ""} />
                    <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "28px 10px 9px", background: "linear-gradient(180deg, rgba(10,10,10,0), rgba(10,10,10,.72))", color: ADR.card, textAlign: "left" }}>
                      <span style={{ display: "inline-block", fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "0.8px", background: "rgba(10,10,10,.55)", padding: "2px 5px", borderRadius: 4 }}>{photoStamp(photo.capturedAt)}</span>
                      {photo.description && <span style={{ display: "block", fontSize: 12, fontWeight: 600, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{photo.description}</span>}
                    </span>
                  </a>
                  {photo.type === "image" && (
                    <button
                      type="button"
                      className="kb review-tile-remove"
                      aria-label={`Remove photo ${photoStamp(photo.capturedAt)}`}
                      title="Remove"
                      disabled={!configured || mediaBusy || decisionBusy}
                      onClick={() => void changeMedia({ removeId: photo.id })}
                      style={{ position: "absolute", right: 8, top: 8, width: 28, height: 28, borderRadius: 14, border: 0, padding: 0, background: "rgba(255,253,248,.95)", color: ADR.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    >
                      <MinusCircle size={16} aria-hidden />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          // No photos: one quiet dashed row (design), not a panel competing
          // with the record for attention.
          <button
            type="button"
            className="kb review-attach-row"
            data-testid="review-add-photos"
            aria-haspopup="dialog"
            aria-expanded={photoPickerOpen}
            disabled={!configured || mediaBusy || decisionBusy}
            onClick={() => { setMediaError(null); setPhotoPickerOpen(true); }}
            style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", height: 38, marginTop: 12, padding: "0 12px", boxSizing: "border-box", borderRadius: 10, fontFamily: "inherit", fontSize: 12.5, cursor: "pointer", textAlign: "left" }}
          >
            <Camera size={13} strokeWidth={1.9} aria-hidden style={{ flex: "none" }} />
            <span data-testid="review-attach-label" style={{ flex: "none", whiteSpace: "nowrap", fontWeight: 700, color: RV.ink2 }}>{mediaBusy ? "Updating photos…" : "Attach photos or videos"}</span>
            <span className="review-attach-hint" data-testid="review-attach-hint" style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>from this walk, the gallery, or a new capture</span>
            <Plus size={12} strokeWidth={2.2} aria-hidden style={{ flex: "none" }} />
          </button>
        )}
        {mediaError && <div role="alert" style={{ marginTop: 8, color: C.red, fontSize: 13.8 }}>{mediaError}</div>}
        {photoPickerOpen && <RecordPhotoPicker visitId={item.visitId} walkName={row.walkName} recordTitle={title} attachedIds={photos.map(photo => photo.id)} search={search} busy={mediaBusy} error={mediaError}
          onClose={() => setPhotoPickerOpen(false)}
          onAttach={async mediaIds => { if (await changeMedia({ mediaIds })) setPhotoPickerOpen(false); }}
          onUpload={async files => { if (await changeMedia({ files })) setPhotoPickerOpen(false); }}
        />}

        {autodeskDestinations && (
          <AutodeskAnswerPreview item={item} destinations={autodeskDestinations} photos={photos} />
        )}
      </div>

      {item.kind === "punch" && punchSave.error && <div role="alert">{punchSave.error} <button onClick={() => void punchSave.retry()}>Retry save</button></div>}
      {/* ---- the decision ---- */}
      <div style={{ padding: "12px 18px 14px", marginTop: 14, borderTop: "1px solid rgba(20,17,12,.07)" }}>
        <ReviewDecisions
          write={row.write}
          title={title}
          search={search}
          configured={configured}
          disabled={mediaBusy || mediaRefreshFailed || (item.kind === "punch" && (punchSave.saving || !!punchSave.error))}
          onBusyChange={setDecisionBusy}
          onValidationError={setValidationFieldKeys}
          note={note}
          patch={Object.keys(decisionPatch).length > 0 ? decisionPatch : undefined}
          onDecided={onDecided}
          variant="card"
          acceptLabel={conflict ? manpowerConflictActionLabel(conflict.choice, "Accept") : undefined}
          visitId={row.item.visitId ?? undefined}
          jobSiteId={row.item.jobSiteId ?? undefined}
        />
      </div>
      {photoViewer !== null && viewerPhotos[photoViewer] && (
        <PhotoFullscreenViewer
          photos={viewerPhotos}
          index={photoViewer}
          onStep={setPhotoViewer}
          onClose={() => setPhotoViewer(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function ReviewQueue(props: ConsoleViewProps) {
  const {
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefWithParam,
    onNavigate,
    onOpenWalk,
  } = props;

  /** Bumped after every landed decision, so the queue re-reads the SERVER's
   *  state rather than trusting an optimistic local guess — a row that showed
   *  "Accepted" because you clicked Accept, while the record is still
   *  pending, is precisely the failure this console exists to remove. */
  const [decisions, setDecisions] = useState(0);
  const [completion, setCompletion] = useState<ReviewCompletionData | null>(null);
  /** Walk photos' pending deletes, per walk. Held here, not in the pane: a
   *  Convert unmounts the pane while its new record is reviewed. */
  const [walkPhotoDrafts, setWalkPhotoDrafts] = useState<Record<string, WalkPhotosDraft>>({});
  const settled = useRef(new Map<string, { walkKey: string; decision: ReviewDecision; startedAt: number }>());
  const [projectOpen, setProjectOpen] = useState(false);
  /** The cards' transition between walks — see `selectWalk`. `leaving` is the
   *  walk still being PAINTED while it animates out, which is not the walk the
   *  URL names by then. */
  const [slide, setSlide] = useState<{
    leaving: string | null;
    dir: SlideDir;
    phase: "out" | "in";
  } | null>(null);
  const slideTimer = useRef<number | undefined>(undefined);
  // A pending exit must not fire into an unmounted tree — the timer navigates.
  useEffect(() => () => window.clearTimeout(slideTimer.current), []);

  /**
   * The search WITHOUT `reviewing` — what this page actually loads against.
   *
   * Which walk you are looking at is a selection over data already in hand,
   * not a question for the server: `getReviewQueue` does not read `reviewing`
   * at all, and `current`/`currentRows` below are derived from `data` right
   * here. But `reviewing` lives in the URL (so a walk survives a reload and a
   * shared link still opens the right one), and the resource key used to be
   * the whole of `search` — so every click on a walk changed the key.
   *
   * `useResource` reads a changed key as a NAVIGATION, which clears `data`,
   * and `if (!data) return <LoadingPanel />` below then blanked the entire
   * workspace. Selecting a walk therefore tore the page down, went to the
   * network, and rebuilt it — a full-width flash between two states that
   * differ by which cards are listed. Dropping the parameter from the key
   * makes the selection what it always was: instant, and free.
   *
   * It is dropped from the FETCH too, not just the key. Sending a parameter
   * the route ignores is how a key and its request drift apart later.
   */
  const dataSearch = withParam(search, REVIEWING_PARAM, null);
  useEffect(() => { setCompletion(null); settled.current.clear(); setLocalDecided({}); retainedRef.current = null; }, [dataSearch]);

  /**
   * Decisions made on this page, folded into one-line rows at once — before
   * the queue re-reads. Each yields to the server's own account of the record
   * as soon as a read made after it lists that record (pending again, or
   * decided), so this only ever bridges the round trip. A walk the last
   * decision emptied is no longer in the queue at all; these rows are what
   * its finished screen is made of.
   */
  const [localDecided, setLocalDecided] = useState<Record<string, LocalDecision>>({});
  /** A walk's saved-for-later section, as the reader last left it. */
  const [savedShown, setSavedShown] = useState<Record<string, boolean>>({});
  const [undoState, setUndoState] = useState<{ busy: string | null; error: { id: string; message: string } | null }>({ busy: null, error: null });
  /** The walk on screen when its last record was decided, held so the pane
   *  ends on that walk rather than jumping to the next. */
  const retainedRef = useRef<{ walk: ReviewWalkResponse; index: number; generatedAt: number } | null>(null);
  const previousWalksRef = useRef<{ walks: ReviewWalkResponse[]; generatedAt: number }>({ walks: [], generatedAt: 0 });
  const lastShownRef = useRef<string | null>(null);

  // Coalesced: the queue read takes seconds, and on a busy workspace live
  // events land faster than that. A refresh that dropped the in-flight read
  // would never let the first one paint.
  const { data: read, failure, refreshFailure } =
    useCoalescedResource<ReviewQueueResponse>(
      `review${dataSearch}`,
      () => fetchReviewQueue(dataSearch),
      refreshKey + decisions,
    );
  /**
   * A review write purges the session cache (LED-638: other views must not
   * keep a snapshot that still shows the record undecided), and a purged
   * reader starts empty. This page re-reads at once and has already folded
   * the decision into its own rows, so it holds what it is showing across a
   * purge that ITS OWN review write caused, until that re-read lands.
   * Anything else that purges — a scope or permission change — still empties
   * it.
   */
  const heldRef = useRef<{ key: string; data: ReviewQueueResponse } | null>(null);
  const holdRef = useRef(false);
  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ purge?: boolean; path?: string }>).detail;
      if (detail?.purge && detail.path?.startsWith("/api/review")) holdRef.current = true;
    };
    window.addEventListener(DATA_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, onChanged);
  }, []);
  if (read && read !== heldRef.current?.data) {
    heldRef.current = { key: dataSearch, data: read };
    holdRef.current = false;
  }
  // Never another scope's: a navigation is not a refresh of this page.
  const data = read ?? (holdRef.current && heldRef.current?.key === dataSearch ? heldRef.current.data : null);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <ReviewQueueSkeleton />;

  const now = new Date(data.generatedAt);
  const generatedAt = now.getTime();
  // Hold the walk whose last record was just decided (see `retainedRef`);
  // let it go once it is back in the queue — an Undo brought a record back.
  if (retainedRef.current && data.walks.some((walk) => walk.key === retainedRef.current!.walk.key)) {
    retainedRef.current = null;
  }
  const lastShown = lastShownRef.current;
  if (
    !retainedRef.current && lastShown && !data.walks.some((walk) => walk.key === lastShown) &&
    Object.values(localDecided).some((entry) => entry.walkKey === lastShown)
  ) {
    const previous = previousWalksRef.current;
    const index = previous.walks.findIndex((walk) => walk.key === lastShown);
    if (index >= 0) retainedRef.current = { walk: previous.walks[index]!, index, generatedAt: previous.generatedAt };
  }
  previousWalksRef.current = { walks: data.walks, generatedAt };
  const retained = retainedRef.current;
  // A held walk is out of the queue because nothing on it is pending.
  const walks = retained
    ? [...data.walks.slice(0, retained.index), { ...retained.walk, total: 0 }, ...data.walks.slice(retained.index)]
    : data.walks;
  /** The rows still WAITING. `not_reviewed` only: the server emits no other
   *  group today (spec §5), and a decided record on this page would be a
   *  card offering to decide it again. */
  const rows =
    data.groups
      .find((group) => group.id === "not_reviewed")
      ?.sites.flatMap((site) => site.rows) ?? [];
  /** What a reviewer set aside (here or on the phone): still reviewable,
   *  never counted as waiting. */
  const savedRows =
    data.groups
      .find((group) => group.id === "saved_for_later")
      ?.sites.flatMap((site) => site.rows) ?? [];
  /** Everything the queue holds across every project — the `all` project tab
   *  is over the whole allowed set, which is exactly this. */
  const total = data.kindTabs.find((tab) => tab.id === "all")?.count ?? 0;
  const pending = rows.length;

  /** True when the org's Record types policy has turned Daily logs off — in
   *  which case there is nothing this queue could ever list, and saying so is
   *  the difference between a policy and a bug. */
  const logsOff = !data.activeKinds.includes("log");

  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const asked = params.get(REVIEWING_PARAM);
  /** The walk on screen: the one the URL names while it still has records, and
   *  otherwise the first — a reviewer who empties a walk should land on the
   *  next one rather than on a page that has gone blank. */
  const current = walks.find((walk) => walk.key === asked) ?? (retained ? walks.find((walk) => walk.key === retained.walk.key) : undefined) ??
    // The walk already on screen stays (its finished card is the answer to
    // its last decision); else one with records waiting before one holding
    // only saved ones.
    (lastShown ? walks.find((walk) => walk.key === lastShown) : undefined) ??
    walks.find((walk) => walk.total > 0) ?? walks[0] ?? null;
  lastShownRef.current = current?.key ?? null;
  /** A local decision stands until a read made after it says otherwise. */
  const foldedLocally = (id: string) => {
    const entry = localDecided[id];
    return !!entry && generatedAt < entry.at;
  };
  const rowsOf = (key: string) =>
    rows.filter(
      (row) => (row.item.visitId ?? `no-walk:${row.item.jobSiteId}`) === key && !foldedLocally(row.item.id),
    );
  /** Folded while the walk has records waiting; open once it has none. */
  const savedOpen = (key: string | null) => !!key && (savedShown[key] ?? rowsOf(key).length === 0);
  const savedOf = (key: string) =>
    savedRows.filter(
      (row) => (row.item.visitId ?? `no-walk:${row.item.jobSiteId}`) === key && !foldedLocally(row.item.id),
    );
  /** When the walk's server figures were read: now, or for a held walk the
   *  last read that still had it. */
  const readAt = (walk: ReviewWalkResponse) => (retained?.walk.key === walk.key ? retained.generatedAt : generatedAt);
  /** This page's decisions on a walk that its server figures predate. */
  const newerLocal = (walk: ReviewWalkResponse) =>
    Object.entries(localDecided).filter(([id, entry]) =>
      entry.walkKey === walk.key && entry.at > readAt(walk) && !(walk.decidedRows ?? []).some((row) => row.id === id));
  /** The walk's decided records: the server's, and this page's newer ones. */
  const decidedOf = (walk: ReviewWalkResponse): DecidedEntry[] => {
    const byId = new Map<string, DecidedEntry>();
    // A record drawn as a card — waiting, or saved for later — is not also a
    // folded "Later" row.
    const pendingIds = new Set([...rowsOf(walk.key), ...savedOf(walk.key)].map((row) => row.item.id));
    for (const entry of walk.decidedRows ?? []) {
      if (!pendingIds.has(entry.id)) byId.set(entry.id, { ...entry, at: localDecided[entry.id]?.at });
    }
    for (const [id, entry] of Object.entries(localDecided)) {
      if (entry.walkKey !== walk.key || pendingIds.has(id)) continue;
      const server = byId.get(id);
      if (!server || readAt(walk) < entry.at) {
        byId.set(id, { ...entry.row, ...(server?.undoUntil ? { undoUntil: server.undoUntil } : {}), at: entry.at, local: !server });
      }
    }
    return [...byId.values()];
  };
  /** The server's receipt decides; a decision it has not read yet was made
   *  a moment ago, so its window is open. */
  const canUndo = (entry: DecidedEntry) =>
    entry.undoUntil ? Date.parse(entry.undoUntil) > Date.now() : !!entry.local && entry.at !== undefined && Date.now() - entry.at < UNDO_WINDOW_MS;
  /** When a decision was made: this page's clock, or its receipt's start. */
  const decidedAt = (entry: DecidedEntry) =>
    entry.at ?? (entry.undoUntil ? Date.parse(entry.undoUntil) - UNDO_WINDOW_MS : 0);
  const undoDecision = async (entry: ReviewDecidedRowResponse) => {
    if (undoState.busy) return;
    setUndoState({ busy: entry.id, error: null });
    const result = await postReviewRestore(dataSearch, { kind: entry.kind, itemId: entry.id });
    if (!result.ok) {
      setUndoState({ busy: null, error: { id: entry.id, message: result.error || "This decision can no longer be undone." } });
      return;
    }
    settled.current.delete(entry.id);
    setLocalDecided((all) => { const next = { ...all }; delete next[entry.id]; return next; });
    setUndoState({ busy: null, error: null });
    setDecisions((count) => count + 1);
  };
  const currentRows = current ? rowsOf(current.key) : [];
  /** A walk's daily-log Photos cards are not reviewed card by card: once
   *  nothing else in the walk is waiting, they become the Walk photos step
   *  (the phone's last review screen, design "Walk Photos Review Web"). */
  const isPhotosCardRow = (row: ReviewRowResponse) =>
    row.item.kind === "log" && row.item.category === "photos" &&
    // Every project has a Walk photos step, as on the phone's walk page
    // (`VisitPage.walkPhotosIntegration`, a81a1a055): the last look before
    // photos go to Procore's daily log, the Autodesk daily report's
    // References, or — unlinked — Led AI's own daily log and gallery, where
    // the card saves and its trash deletes.
    // Only a card made once the step was live for that integration (LED-711):
    // an older one stays an ordinary card — the review its walk had when it
    // was made. An unlinked project uses Procore's line, as `VisitPage` does.
    isWalkPhotosStepCard(
      { scope: "daily_log", category: row.item.category, createdAt: row.item.createdAt },
      walkPhotosIntegrationOf(row.item.syncSource) ?? "local",
    );
  /** The kind filter is applied by the SERVER, so under one a walk's other
   *  pending records are simply absent from `rows` — "only Photos cards
   *  left" can't be told from "only Photos cards shown". Filtered, Photos
   *  cards are ordinary cards again. */
  const walkPhotosMode = data.filter.kind === "all";
  const isWalkPhotosStep = (key: string | null) => {
    if (!walkPhotosMode || !key || key.startsWith("no-walk:")) return false;
    const walkRows = rowsOf(key);
    return walkRows.length > 0 && walkRows.every(isPhotosCardRow);
  };
  /** A decision landed: fold its card where it sat, and re-read. The walk's
   *  own finished screen reports the outcome (`ReviewDoneCard`). */
  const didDecide = (row: ReviewRowResponse, decision: ReviewDecision, startedAt: number) => {
    const walkKey = row.item.visitId ?? `no-walk:${row.item.jobSiteId}`;
    settled.current.set(row.item.id, { walkKey, decision, startedAt });
    const folded: DecidedState = decision === "rejected" ? "rejected" : decision === "deferred" ? "deferred" : "accepted";
    setLocalDecided((all) => ({
      ...all,
      [row.item.id]: {
        walkKey,
        at: Date.now(),
        row: {
          id: row.item.id,
          kind: row.write.kind,
          ...(row.item.category ? { category: row.item.category } : {}),
          ...(row.item.recordType ? { recordType: row.item.recordType } : {}),
          title: row.item.title,
          decision: folded,
          createdAt: String(row.item.createdAt),
        },
      },
    }));
    setUndoState((state) => (state.error ? { ...state, error: null } : state));
    setDecisions((count) => count + 1);
  };
  /** Walk photos' Send finishes a walk. One whose cards were decided here
   *  ends on its own finished screen; one that was only photos says so in
   *  the summary banner. */
  const didSendWalkPhotos = (walkKey: string) => {
    const walk = walks.find(walk => walk.key === walkKey);
    if (walk && !Object.values(localDecided).some((entry) => entry.walkKey === walkKey)) finishWalk(walk);
  };
  function finishWalk(walk: (typeof walks)[number]) {
    const walkKey = walk.key;
    // Refetched queue totals include earlier reviews. This banner reports
    // only decisions made here, including cards removed by earlier refetches.
    const accepted: ReviewCompletionData["accepted"] = [];
    let rejected = 0;
    let deferred = 0;
    for (const [id, result] of settled.current) {
      if (result.walkKey !== walkKey) continue;
      if (result.decision === "approved" || result.decision === "edited") accepted.push({ id, startedAt: result.startedAt });
      else if (result.decision === "rejected") rejected += 1;
      else deferred += 1;
    }
    setCompletion({ key: walk.key, name: walk.walkName || walk.siteName || "Walk", accepted, rejected, deferred, hasDeferredItems: !!walk.deferred });
    for (const [id, result] of settled.current) {
      if (result.walkKey === walkKey) settled.current.delete(id);
    }
  }
  /**
   * The walk the CARDS are showing, which lags the selection by one exit.
   *
   * Everything else on the page — the highlighted row on the left, the
   * progress bar, the header — follows `current`, so it answers the click in
   * the same frame. Only this one region holds the previous walk for the
   * length of its exit animation, because a card cannot animate out of a list
   * it has already been removed from.
   */
  const shownKey =
    slide?.phase === "out" && slide.leaving
      ? slide.leaving
      : (current?.key ?? null);
  const shownWalkPhotos = isWalkPhotosStep(shownKey);
  const shownWalk = shownKey ? walks.find((walk) => walk.key === shownKey) : undefined;
  const shownRows = shownKey
    ? rowsOf(shownKey).filter((row) => !walkPhotosMode || !isPhotosCardRow(row))
    : [];
  /** The walk's records in the order they were made: a card for each still
   *  waiting, a folded row for each decided. */
  const shownDecided = shownWalk ? decidedOf(shownWalk) : [];
  const shownEntries: Array<{ id: string; at: number; card?: ReviewRowResponse; decided?: (typeof shownDecided)[number] }> = [
    ...shownRows.map((card) => ({ id: card.item.id, at: new Date(card.item.createdAt).getTime(), card })),
    ...shownDecided.map((decided) => ({ id: decided.id, at: Date.parse(decided.createdAt), decided })),
  ].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
  /** The newest decision on the walk on screen that can still be undone. */
  const lastUndoable = current
    ? decidedOf(current)
        .filter(canUndo)
        .sort((a, b) => decidedAt(b) - decidedAt(a))[0]
    : undefined;
  // The server's figures, plus this page's decisions they predate.
  //
  // Every CARD in the walk counts once (design "Review", `N OF M REVIEWED`):
  // each record, records made from photos (they are records), and the Walk
  // photos card — which stands for all of the walk's daily-log Photos rows,
  // so those count as ONE card however many rows the server holds.
  const progress = reviewProgress({
    current,
    extra: current ? newerLocal(current).map(([, entry]) => entry.row) : [],
    pending: current ? rowsOf(current.key) : [],
    decidedRows: current ? decidedOf(current) : [],
    isPhotosRow: (row) => walkPhotosMode && isPhotosCardRow(row),
    isPhotosDecided: (row) => walkPhotosMode && row.kind === "log" && row.category === "photos",
  });
  const nextWalk = data.walks.find((walk) => walk.key !== shownKey);
  const shownSaved = shownKey ? savedOf(shownKey).filter((row) => !walkPhotosMode || !isPhotosCardRow(row)) : [];
  const doneSummary = (() => {
    const accepted = shownDecided.filter((entry) => entry.decision === "accepted");
    const deferred = shownDecided.filter((entry) => entry.decision === "deferred").length + shownSaved.length;
    const count = shownDecided.length;
    const day = shownWalk?.startedAt ? dayWithYear(shownWalk.startedAt) : null;
    const later = deferred ? ` ${plural(deferred, "record", "records")} saved for later.` : "";
    // A walk whose decided records are not known (an older backend) says
    // only that it is done.
    if (count === 0) return { deferred, text: "" };
    if (accepted.length === 0) return { deferred, text: `Nothing from this walk was accepted.${later}` };
    const head = `${accepted.length} of ${plural(count, "record", "records")} from this walk accepted`;
    const site = data.sites?.find((entry) => entry.id === shownWalk?.jobSiteId)?.syncSource;
    if (shownWalk?.autodeskDestinations) {
      const issues = accepted.filter((entry) => isAutodeskIssue(entry as unknown as ConsoleRow)).length;
      const report = day ? `the ${day} daily report` : "the day's daily report";
      const rest = accepted.length - issues;
      const where = issues === 0
        ? `they went into ${report}`
        : `${plural(issues, "issue", "issues")} sent to Autodesk${rest ? `; the rest went into ${report}` : ""}`;
      return { deferred, text: `${head} — ${where}.${later}` };
    }
    if (site === "procore") {
      const kinds = accepted.map((entry) => procoreKindOf({ kind: entry.kind, category: entry.category, recordType: entry.recordType as ConsoleRow["recordType"] }).key);
      const records = countPhrase([
        [kinds.filter((key) => key === "observation").length, "observation", "observations"],
        [kinds.filter((key) => key === "punch").length, "punch item", "punch items"],
        [kinds.filter((key) => key === "task").length, "task", "tasks"],
      ]);
      const issues = kinds.filter((key) => key === "observation" || key === "punch" || key === "task").length;
      const log = day ? `the ${day} daily log` : "the day's daily log";
      const rest = accepted.length - issues;
      const where = issues === 0
        ? `they went into ${log}`
        : `${records} sent to Procore${rest ? `; the rest went into ${log}` : ""}`;
      return { deferred, text: `${head} — ${where}.${later}` };
    }
    return { deferred, text: `${head}.${later}` };
  })();

  const activeProject = data.projectTabs.find(
    (tab) => tab.id === data.filter.project,
  );

  /**
   * Selecting a walk is a MOVE between two positions in a list, so it is
   * animated as one: the cards on screen leave in the direction you are
   * travelling, and the next walk's cards arrive from the side you are
   * travelling from. Picking a walk further down sends the current cards up
   * and brings the new ones from below; picking one further up reverses both
   * halves. A single fade would say "something changed"; this says which way.
   *
   * Two phases rather than one, because a fade-in alone leaves the OUTGOING
   * cards vanishing in a single frame — the very thing that made the old
   * behaviour read as a flash. `phase` runs `out` on the current content,
   * navigates when that finishes, then `in` on what replaces it.
   *
   * The navigation is DEFERRED by the length of the exit, which is the only
   * part of this worth being careful about: with motion off there must be no
   * delay at all, or the console would feel slower to precisely the people
   * who asked for less animation and get none of the thing they are waiting
   * for. `reduced()` is checked at click time rather than cached, so
   * flipping the OS setting takes effect on the next click.
   */
  const selectWalk = (key: string) => {
    if (key === current?.key) return;
    // Leaving a finished walk lets it go from the list.
    if (retainedRef.current && retainedRef.current.walk.key !== key) retainedRef.current = null;
    const to = walks.findIndex((walk) => walk.key === key);
    const from = walks.findIndex((walk) => walk.key === current?.key);
    const dir: SlideDir = to > from ? "down" : "up";

    // The URL moves NOW, in the click's own frame. An earlier cut of this
    // held the navigation back for the length of the exit, which also held
    // back the selected row on the left — the walk you had just clicked sat
    // unhighlighted for 140ms, and the page read as slow at the exact moment
    // it was meant to feel deliberate. Selection is feedback; only the CARDS
    // are choreography.
    onNavigate(hrefWithParam(REVIEWING_PARAM, key));
    if (reduced()) {
      setSlide(null);
      return;
    }
    window.clearTimeout(slideTimer.current);
    // Keep painting the walk being LEFT until its exit has played out.
    setSlide({ leaving: current?.key ?? null, dir, phase: "out" });
    slideTimer.current = window.setTimeout(
      () => setSlide({ leaving: null, dir, phase: "in" }),
      SLIDE_OUT_MS,
    );
  };

  /** The class the cards wear this render — see `selectWalk`. `null` on a
   *  plain load, so a first paint does not animate as if you had chosen it. */
  const slideClass =
    slide === null
      ? undefined
      : `rev${slide.phase === "out" ? "out" : "in"}-${slide.dir}`;
  const projectHref = (id: string) => {
    // Share the shell's project scope. The server prefers `site` over the
    // legacy `project` filter, so changing only `project` leaves the queue
    // stuck on the sidebar's selection, including when choosing All projects.
    let href = hrefWithParam("site", id === "all" ? null : id);
    for (const key of ["project", "walk", REVIEWING_PARAM, "reviewItem"]) {
      href = withParam(href, key, null);
    }
    return href;
  };
  const selectProject = (id: string) => {
    setProjectOpen(false);
    onNavigate(projectHref(id));
  };

  return (
    <div
      data-testid="review-workspace"
      // The page ground carries both columns now; the walk list's own
      // hairline is what divides them.
      // NOT `flex-wrap: wrap`. A fixed 400px column beside a flexible pane
      // wraps on a narrow viewport, and a wrapped second line makes this
      // container taller than the bounded height its two scrollers are
      // measured against — so neither scrolls and the pane runs off the page.
      // The pane shrinks instead (`minWidth: 0`), which is what it is for.
      style={{ flex: 1, minHeight: 0, display: "flex", background: C.pageGround }}
    >
      <style>{REVIEW_CSS}</style>
      {/* ══════════ the walks ══════════ */}
      <div
        data-testid="review-walk-column"
        style={{
          width: WALK_COLUMN,
          flexShrink: 0,
          // The SIDEBAR's ground, and its hairline: this column is a second
          // navigation column — a list you pick from, feeding the pane beside
          // it — so it is built out of the chrome the reader already knows
          // rather than out of a tone of its own (user-directed, 2026-09-04).
          background: RV.queueGround,
          borderRight: "1px solid rgba(20,17,12,.08)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            padding: "20px 16px 12px",
            borderBottom: "1px solid rgba(20,17,12,.06)",
            flexShrink: 0,
          }}
        >
          <h1
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: "-0.5px",
              lineHeight: 1.1,
            }}
          >
            Review queue
          </h1>
          <div data-testid="review-queue-summary" style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "1.1px", textTransform: "uppercase", color: C.sub, marginTop: 5, lineHeight: 1.5 }}>
            {[
              plural(data.walks.length, "walk", "walks"),
              `${plural(pending, "item", "items")} to review`,
              // Always stated, zero included — the design's third figure.
              `${savedRows.length} saved for later`,
              // Only when your share is a DIFFERENT number from the queue's
              // own total — otherwise it is the same fact twice.
              reviewCountNote(total, pending),
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {/* WHOSE queue this is, stated on the page rather than left to be
              inferred: "nothing to review" over a workspace full of a
              teammate's unreviewed records reads as a bug (spec §5). */}
          <div
            data-testid="review-scope-note"
            style={{
              fontFamily: FONT_MONO,
              fontSize: 8.5,
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: RV.meta,
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            Records stay private until reviewed and accepted.
          </div>

          {/* The project filter, as the design's picker rather than a chip
              strip: one control that says what it is filtered to, with the
              count beside every option. Still URL state. */}
          <div style={{ position: "relative", marginTop: 12 }}>
            <button
              type="button"
              className="kb"
              data-testid="review-project-picker"
              aria-haspopup="menu"
              aria-expanded={projectOpen}
              onClick={() => setProjectOpen((open) => !open)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 38,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(20,17,12,.13)",
                background: ADR.card,
                textAlign: "left",
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13.5,
                  fontWeight: 600,
                  letterSpacing: "-0.2px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {activeProject?.name ?? "All projects"}
              </span>
              <span
                data-testid="review-project-count"
                style={{ fontFamily: FONT_MONO, flexShrink: 0, fontSize: 10, color: ADR.label }}
              >
                {activeProject?.count ?? total}
              </span>
              <svg
                width="11"
                height="7"
                viewBox="0 0 10 6"
                fill="none"
                style={{
                  flexShrink: 0,
                  transform: projectOpen ? "rotate(180deg)" : undefined,
                  transition: "transform 0.14s",
                }}
              >
                <path
                  d="M1 1l4 4 4-4"
                  stroke={C.sub}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {projectOpen && (
              <div
                role="menu"
                data-testid="review-project-menu"
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 47,
                  zIndex: 20,
                  background: C.surface,
                  border: `1px solid ${C.line2}`,
                  borderRadius: 12,
                  boxShadow: `0 14px 32px ${C.line}`,
                  padding: 5,
                }}
              >
                {data.projectTabs.map((tab) => {
                  const on = tab.id === data.filter.project;
                  return (
                    <a
                      key={tab.id}
                      role="menuitemradio"
                      aria-checked={on}
                      className="kb navrow"
                      data-testid="review-project-option"
                      data-project-id={tab.id}
                      href={projectHref(tab.id)}
                      onClick={(event) => {
                        event.preventDefault();
                        selectProject(tab.id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                        height: 38,
                        padding: "0 10px",
                        borderRadius: 8,
                        ...(on ? { background: C.faint } : {}),
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 16.1,
                          fontWeight: on ? 700 : 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {tab.name}
                      </span>
                      <span style={{ ...META, fontSize: 10.93, color: C.sub }}>
                        {tab.count}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div
          className="sb"
          data-testid="review-walk-list"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            // The fixed Ask composer spans this pane's bottom. Every
            // scrolling body in the console ends in its clearance so the last
            // row can be scrolled clear of it; these two had lost theirs,
            // which is what "scrolling on the review page is not working"
            // was — you reach the bottom and the last card is still under it.
            padding: `4px 8px ${ASK_COMPOSER_CLEARANCE}px`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 4 }}>
            {walks.map((walk) => (
              <WalkCard
                key={walk.key}
                walk={walk}
                active={current?.key === walk.key}
                onSelect={() => selectWalk(walk.key)}
              />
            ))}

            {walks.length === 0 && (
              <div
                data-testid="review-walks-empty"
                style={{
                  background: C.ink,
                  borderRadius: 14,
                  padding: "22px 18px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    fontSize: 19.55,
                    fontWeight: 700,
                    letterSpacing: "-0.5px",
                    color: C.surface,
                  }}
                >
                  {logsOff ? "Daily logs are off." : "Nothing to review."}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 14.95,
                    color: C.sub2,
                    marginTop: 5,
                    lineHeight: 1.45,
                  }}
                >
                  {logsOff
                    ? "This organization's Record types policy has Daily logs turned off, so no walk produces one."
                    : total === 0
                      ? "No items are waiting in this queue. Items saved for later still need a decision. New walks land here as Led finishes them."
                      : "Nothing on this project. Pick another above — you have items waiting elsewhere."}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════ one walk's records ══════════ */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {completion && <ReviewCompletion result={completion} search={dataSearch} onDismiss={() => setCompletion(null)} />}
        {current ? (
          <>
            <div
              data-testid="review-walk-head"
              style={{
                flexShrink: 0,
                padding: "18px 32px 0",
                background: C.pageGround,
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: REVIEW_CONTENT_WIDTH,
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                }}
              >
              <div style={{ flex: 1, minWidth: 0 }}>
              {/* `{WALK} · {N} MIN WALK` and the badge on ONE line: the kicker
                  truncates, the badge never wraps (design "Review"). */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <span data-testid="review-walk-kicker" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "1.2px", textTransform: "uppercase", color: C.sub }}>
                  {[
                    current.walkName ?? walkWhen(current.startedAt, now),
                    current.durationMinutes !== undefined
                      ? `${current.durationMinutes} min walk`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span
                  data-testid="review-walk-badge"
                  style={{ flex: "none", whiteSpace: "nowrap", fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: RV.badgeInk, background: RV.badgeBg, padding: "2px 6px", borderRadius: 4 }}
                >
                  {progress.left === 0 ? "Reviewed" : "In review"}
                </span>
              </div>
              <h2
                style={{
                  margin: "6px 0 0",
                  fontSize: 20,
                  fontWeight: 800,
                  letterSpacing: "-0.5px",
                  lineHeight: 1.15,
                }}
              >
                {current.siteName || current.jobSiteId}
              </h2>
              </div>
              {/* Undo the walk's most recent decision still inside its window. */}
              <button
                type="button"
                data-testid="review-undo"
                disabled={!lastUndoable || !!undoState.busy || !data.writesConfigured}
                onClick={() => lastUndoable && void undoDecision(lastUndoable)}
                className="review-outline"
                style={{ flex: "none", height: 28, padding: "0 12px", borderRadius: 14, border: "1px solid rgba(20,17,12,.12)", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: "1.1px", textTransform: "uppercase", color: C.sub, opacity: lastUndoable ? 1 : 0.4, cursor: lastUndoable ? "pointer" : "default" }}
              >
                <Undo2 size={11} aria-hidden />
                Undo
              </button>
              </div>

              {/* How much of THIS walk is done — the question the old
                  status-grouped list could not answer. */}
              <div
                style={{
                  width: "100%",
                  maxWidth: REVIEW_CONTENT_WIDTH,
                  margin: "12px auto 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 2,
                    background: "rgba(20,17,12,.08)",
                    overflow: "hidden",
                    display: "block",
                  }}
                >
                  <span
                    data-testid="review-progress"
                    data-percent={progress.percent}
                    style={{
                      display: "block",
                      height: "100%",
                      width: `${progress.percent}%`,
                      borderRadius: 2,
                      background: RV.green,
                      transition: "width 0.22s ease-out",
                    }}
                  />
                </span>
                <span
                  data-testid="review-progress-label"
                  style={{ fontFamily: FONT_MONO, flexShrink: 0, whiteSpace: "nowrap", fontSize: 9, letterSpacing: "1.1px", textTransform: "uppercase", color: C.sub }}
                >
                  {`${progress.decided} of ${progress.all} reviewed`}
                </span>
              </div>
            </div>

            <div
              className="sb"
              data-testid="review-records"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                // Walk photos pins its own Send bar to the bottom edge.
                padding: `16px 32px ${ASK_COMPOSER_CLEARANCE}px`,
              }}
            >
              {refreshFailure && (
                <div style={{ marginBottom: 14 }}>
                  <StaleBanner
                    failure={refreshFailure}
                    since={data.generatedAt}
                  />
                </div>
              )}

              {/* The write gap, stated once at the top rather than only in a
                  tooltip — a reader must not have to hover a control to learn
                  the page cannot change anything. */}
              {!data.writesConfigured && (
                <div
                  data-testid="review-writes-note"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 13px",
                    borderRadius: 11,
                    background: C.faint,
                    border: `1px solid ${C.line}`,
                    maxWidth: REVIEW_CONTENT_WIDTH, boxSizing: "border-box", margin: "0 auto 14px",
                  }}
                >
                  <span
                    style={{
                      ...META,
                      fontSize: 10.35,
                      color: C.amber,
                      flexShrink: 0,
                    }}
                  >
                    Read only
                  </span>
                  <span
                    style={{ fontSize: 14.37, color: C.sub, lineHeight: 1.5 }}
                  >
                    {data.writesNote}
                  </span>
                </div>
              )}

              <div
                // Keyed on the walk so the ENTER animation replays: a CSS
                // animation restarts on a new element, and these cards are
                // the same element with different children otherwise. The
                // exit runs before the key changes, on the outgoing content.
                key={shownKey ?? "no-walk"}
                className={slideClass}
                data-testid="review-cards"
                style={{
                  width: "100%",
                  maxWidth: REVIEW_CONTENT_WIDTH,
                  margin: "0 auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >

                {shownEntries.map((entry) =>
                  entry.card ? (
                    <RecordCard
                      key={entry.card.item.id}
                      row={entry.card}
                      search={search}
                      configured={data.writesConfigured}
                      note={data.writesNote}
                      onDecided={(decision, startedAt) => didDecide(entry.card!, decision, startedAt)}
                      autodeskDestinations={shownWalk?.autodeskDestinations}
                      onConverted={() => setDecisions(count => count + 1)}
                    />
                  ) : (
                    <DecidedRow
                      key={entry.decided!.id}
                      id={entry.decided!.id}
                      kind={decidedKind(entry.decided!, shownWalk?.autodeskDestinations
                        ? (row) => autodeskKindOf(row, shownWalk.autodeskDestinations!)
                        : undefined)}
                      title={entry.decided!.title}
                      decision={entry.decided!.decision}
                      canUndo={data.writesConfigured && canUndo(entry.decided!)}
                      busy={undoState.busy === entry.decided!.id}
                      error={undoState.error?.id === entry.decided!.id ? undoState.error.message : null}
                      onUndo={() => void undoDecision(entry.decided!)}
                    />
                  ),
                )}

                {/* Walk photos: the walk's last card (LED-1154), while its
                    Photos cards wait — or are saved for later — and after it
                    was decided here, collapsed to one line. */}
                {walkPhotosMode && shownKey && !shownKey.startsWith("no-walk:") &&
                  ([...rowsOf(shownKey), ...savedOf(shownKey)].some(isPhotosCardRow) || !!walkPhotosOutcome(shownKey)) ? (
                  <WalkPhotosPane
                    key={`walk-photos:${shownKey}`}
                    visitId={shownKey}
                    search={dataSearch}
                    photoCardIds={[...rowsOf(shownKey), ...savedOf(shownKey)].filter(isPhotosCardRow).map((row) => row.item.id)}
                    saved={!rowsOf(shownKey).some(isPhotosCardRow) && savedOf(shownKey).some(isPhotosCardRow)}
                    syncSource={[...rowsOf(shownKey), ...savedOf(shownKey)].find(isPhotosCardRow)?.item.syncSource}
                    refreshKey={decisions}
                    draft={walkPhotoDrafts[shownKey] ?? EMPTY_WALK_PHOTOS_DRAFT}
                    onDraftChange={(update) =>
                      setWalkPhotoDrafts((all) => ({
                        ...all,
                        [shownKey]: update(all[shownKey] ?? EMPTY_WALK_PHOTOS_DRAFT),
                      }))
                    }
                    onSent={() => {
                      if (!rowsOf(shownKey).some((row) => !isPhotosCardRow(row))) didSendWalkPhotos(shownKey);
                      setDecisions((count) => count + 1);
                    }}
                    onConverted={() => setDecisions((count) => count + 1)}
                    hrefForItem={props.hrefForItem}
                    onOpenItem={props.onOpenItem}
                  />
                ) : null}

                {shownRows.length === 0 && !shownWalkPhotos && shownWalk && (
                  <ReviewWalkDone
                    search={dataSearch}
                    accepted={[...settled.current]
                      .filter(([, result]) => result.walkKey === shownWalk.key && (result.decision === "approved" || result.decision === "edited"))
                      .map(([id, result]) => ({ id, startedAt: result.startedAt }))}
                    title={shownSaved.length ? "Only records saved for later are left" : "Walk reviewed"}
                    summary={doneSummary.text}
                    primary={{
                      label: shownWalk.autodeskDestinations ? "Open the daily report" : "Open the daily log",
                      href: dailyReportHref(dataSearch, shownWalk.jobSiteId, shownWalk.startedAt),
                      onClick: () => onNavigate(dailyReportHref(dataSearch, shownWalk.jobSiteId, shownWalk.startedAt)),
                    }}
                    secondary={nextWalk ? { label: "Next walk", onClick: () => selectWalk(nextWalk.key) } : undefined}
                  />
                )}

                {shownSaved.length > 0 && (
                  <SavedForLaterHeader
                    count={shownSaved.length}
                    open={savedOpen(shownKey)}
                    onToggle={() => setSavedShown((all) => ({ ...all, [shownKey!]: !savedOpen(shownKey) }))}
                  />
                )}
                {shownSaved.length > 0 && savedOpen(shownKey) &&
                  shownSaved.map((row) => (
                    <RecordCard
                      key={row.item.id}
                      row={row}
                      search={search}
                      configured={data.writesConfigured}
                      note={data.writesNote}
                      onDecided={(decision, startedAt) => didDecide(row, decision, startedAt)}
                      autodeskDestinations={shownWalk?.autodeskDestinations}
                      onConverted={() => setDecisions(count => count + 1)}
                    />
                  ))}

              </div>
            </div>
          </>
        ) : (
          <div
            data-testid="review-empty"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 40,
            }}
          >
            <div style={{ maxWidth: 420 }}>
              <EmptyPanel
                title={
                  logsOff
                    ? "Daily logs are turned off for this organization"
                    : total === 0
                      ? "You have nothing to review"
                      : "Nothing to review on this project"
                }
                body={
                  logsOff
                    ? "Your company's Record types policy does not include daily logs, so no walk produces one. An admin can turn them back on from Policies."
                    : total === 0
                      ? data.availableWorkspaces.length > 1
                        ? "This queue holds YOUR unreviewed daily logs. None are waiting in this workspace — a teammate's records are theirs to review. Try another workspace from the switcher."
                        : "This queue holds YOUR unreviewed daily logs. None are waiting here. Items saved for later still need a decision."
                      : "You have daily logs waiting, just not on this project. Clear the project filter on the left."
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Rewrite one query parameter of an href the shell already built.
 *
 * The shell's `hrefWithParam` rewrites one key of the CURRENT url; changing
 * two at once needs a second pass over its answer, which is what this is. Same
 * rule as `Shell`'s own `hrefWith`: a returned bare pathname is not a query.
 */
function withParam(href: string, key: string, value: string | null): string {
  const at = href.indexOf("?");
  const base = at >= 0 ? href.slice(0, at) : href;
  const params = new URLSearchParams(at >= 0 ? href.slice(at + 1) : "");
  if (value === null) params.delete(key);
  else params.set(key, value);
  const query = params.toString();
  return query ? `${base}?${query}` : base || "?";
}

/**
 * How much of one walk is reviewed, counting CARDS (LED-1153): every record,
 * records converted from photos, and the Walk photos card once — the walk's
 * daily-log Photos rows are one card on screen, so they are one in the count.
 *
 * The server's per-walk figures are the base (`decided`, `deferred`), plus
 * this page's decisions they predate (`extra`). A deferred record stays in
 * the denominator without counting as reviewed. `left` is what still waits.
 */
export function reviewProgress(args: {
  current: Pick<ReviewWalkResponse, "decided" | "deferred"> | null | undefined;
  extra: Array<Pick<ReviewDecidedRowResponse, "decision">>;
  pending: ReviewRowResponse[];
  decidedRows: Array<Pick<ReviewDecidedRowResponse, "kind" | "category" | "decision">>;
  isPhotosRow: (row: ReviewRowResponse) => boolean;
  isPhotosDecided: (row: Pick<ReviewDecidedRowResponse, "kind" | "category">) => boolean;
}): { decided: number; deferred: number; all: number; left: number; percent: number } {
  if (!args.current) return { decided: 0, deferred: 0, all: 0, left: 0, percent: 100 };
  let decided = args.current.decided + args.extra.filter((row) => row.decision !== "deferred").length;
  let deferred = (args.current.deferred ?? 0) + args.extra.filter((row) => row.decision === "deferred").length;
  let left = args.pending.length;
  const photoPending = args.pending.filter(args.isPhotosRow).length;
  const photoDone = args.decidedRows.filter((row) => args.isPhotosDecided(row));
  const photoDecided = photoDone.filter((row) => row.decision !== "deferred").length;
  const photoDeferred = photoDone.length - photoDecided;
  if (photoPending + photoDone.length > 0) {
    left -= photoPending;
    decided = Math.max(0, decided - photoDecided);
    deferred = Math.max(0, deferred - photoDeferred);
    if (photoPending > 0) left += 1;
    else if (photoDecided > 0) decided += 1;
    else deferred += 1;
  }
  const all = decided + deferred + left;
  return { decided, deferred, all, left, percent: all === 0 ? 100 : Math.round((decided / all) * 100) };
}

/** How much of one walk is decided, as a whole percent. A walk with nothing
 *  left is 100 rather than NaN. */
/** The finished walk, with what this session's accepts have confirmed
 *  syncing to under its summary. */
function ReviewWalkDone({ search, accepted, ...card }: Parameters<typeof ReviewDoneCard>[0] & {
  search: string;
  accepted: Array<{ id: string; startedAt: number }>;
}) {
  const key = accepted.map((entry) => `${entry.id}@${entry.startedAt}`).join(",");
  const result = useMemo(() => ({ accepted }), [key]);
  const sync = useReviewSyncCounts(result, search);
  const synced = syncOutcome(0, sync).filter((part) => part.startsWith("Synced"));
  const waiting = sync.waiting ? `${sync.waiting} ${sync.waiting === 1 ? "item has" : "items have"} no confirmed sync yet.` : "";
  const outcome = [synced.join(" · "), waiting].filter(Boolean).join(" ");
  return <ReviewDoneCard {...card} outcome={outcome || undefined} />;
}

/** `"2026-10-17T…"` → `"Oct 17, 2026"`: the day a walk's report is filed under. */
function dayWithYear(startedAt: string): string | null {
  const at = new Date(startedAt);
  if (Number.isNaN(at.getTime())) return null;
  return `${MONTHS[at.getMonth()]} ${at.getDate()}, ${at.getFullYear()}`;
}
